// Lightweight i18n for Phase 0 auth screens.
//
// Strings are copy-pasted from the design handoff
// `design/UI : UX v1.0/for-engineering-auth/specs/screen-strings.md` —
// EVERY visible label, headline, button text, footer string. Anything not
// in this file should not appear on screen (see memory
// feedback-design-handoff-strict).
//
// Two locales: EN + FR. When admin scope grows past auth, swap this for
// react-intl, lingui, or i18next.

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export type Lang = 'EN' | 'FR';

type StringTree = { readonly [key: string]: string | StringTree };

const en = {
  common: {
    or: 'OR',
    back: 'Back',
    signOut: 'Sign out',
  },
  brand: {
    // Admin-flavor chrome per design/UI : UX v1.0/for-engineering-auth-admin-consumer/
    // specs/screen-strings.md § SHARED · Brand panel · Admin flavor.
    // The CSS in @gc-coach/design-system handles mono-casing the wordmark
    // via text-transform; the raw value can stay "GC Coach" if we want,
    // but the spec writes it as "GC COACH" so we mirror that for clarity.
    wordmark: 'GC COACH',
    wordmarkBadge: 'ADMIN',
    wordmarkSub: 'INTERNAL BACK OFFICE',
    eyebrow: 'INTERNAL TOOL · RESTRICTED ACCESS',
    headline: 'Administration console.',
    tagline:
      'Content, tenant and knowledge-base management. For the internal GC Coach team only.',
    footerLeft: 'ENV · STAGING',
    footerRight: 'EU · FR / EN',
    utilityBarLeft: 'GC COACH · ADMIN CONSOLE',
    utilityBarRight: 'INTERNAL ACCESS · admin.gc-coach.ai',
  },
  login: {
    eyebrow: 'SIGN IN',
    headline: 'Welcome back',
    emailLabel: 'WORK EMAIL',
    emailPlaceholder: 'florence.larrieu@acme.com',
    passwordLabel: 'PASSWORD',
    rememberDevice: 'Remember this device',
    forgotPassword: 'Forgot password?',
    submitButton: 'Sign in →',
    ssoMicrosoft: 'Continue with Microsoft',
    ssoGoogle: 'Continue with Google',
    ssoEnterprise: 'Single sign-on',
    footerLine: 'No account yet? Access to GC Coach is granted through your legal department.',
    footerLink: 'Request access',
    errorInvalidCredentials: 'That email or password didn’t work. Try again.',
    errorRateLimited: 'Too many attempts. Wait a moment, then try again.',
  },
  firstLogin: {
    eyebrow: 'WELCOME · FIRST SIGN-IN',
    headline: 'Set your password',
    body:
      'Your account {email} was created by your administrator. Choose a password to finish setup — you’ll set up two-factor verification next.',
    newPasswordLabel: 'NEW PASSWORD',
    confirmPasswordLabel: 'CONFIRM PASSWORD',
    submitButton: 'Set password →',
    callout: 'Next step: set up your authenticator app. Keep your phone handy.',
    errorMismatch: 'Passwords don’t match.',
    errorWeakPassword: 'That password doesn’t meet the policy requirements.',
  },
  mfaChallenge: {
    eyebrow: 'TWO-FACTOR VERIFICATION',
    headline: 'Verify it’s you',
    bodyTemplate: 'Enter the 6-digit code from your authenticator app for {email}.',
    codeLabel: 'AUTHENTICATION CODE',
    trustDeviceTemplate: 'Trust this device for {days} days',
    trustDevicePolicyTemplate: 'ACME POLICY · {days} D MAX',
    submitButton: 'Verify →',
    fallbackEmail: 'Use an email code instead',
    fallbackRecovery: 'Use a backup recovery code',
    fallbackLostDevice: 'I lost my authenticator',
    fallbackHelp: 'I don’t have access to any method',
    errorWrongCode: 'That code didn’t work. Try again.',
  },
  mfaRecoveryCode: {
    eyebrow: 'ACCOUNT RECOVERY',
    headline: 'Check your email',
    bodyTemplate:
      'We sent a 6-digit code to the email on file for {email}. Enter it below to re-enroll a new authenticator.',
    bodyGeneric:
      'We sent a 6-digit code to the email on file for your account. Enter it below to re-enroll a new authenticator.',
    codeLabel: 'RECOVERY CODE',
    submitButton: 'Verify →',
    expiresCaption: 'THE CODE EXPIRES IN 10 MINUTES',
    cancelLink: '← Cancel and sign in differently',
    errorWrongCode: 'That code didn’t work. Try again.',
    errorExpired: 'That code has expired. Start recovery again.',
  },
  mfaSetup: {
    eyebrow: 'ENABLE TWO-FACTOR AUTHENTICATION',
    eyebrowRecovery: 'SET UP A NEW AUTHENTICATOR',
    headlineScan: 'Scan the QR code',
    bodyScan:
      'Open your authenticator app (Google Authenticator, 1Password, Authy…) and scan this code.',
    manualKeyLabel: 'OR ENTER THIS KEY MANUALLY',
    accountCaptionTemplate: 'ACCOUNT · GC COACH ({email})',
    submitScan: 'I’ve scanned — continue →',
    callout:
      'In the next step we’ll give you 10 backup codes to keep offline, in case you lose access to your phone.',
    stepperScan: 'Scan',
    stepperVerify: 'Verify',
    stepperSave: 'Save',
    headlineVerify: 'Verify the code',
    bodyVerify: 'Enter the 6-digit code your authenticator now shows.',
    submitVerify: 'Verify →',
    headlineSave: 'Save your recovery codes',
    bodySave:
      'Store these one-time codes somewhere safe (password manager). Each can be used once if you lose access to your authenticator.',
    download: 'Download codes',
    copy: 'Copy to clipboard',
    confirmSaved: 'I’ve saved these codes',
    finishButton: 'Finish setup',
  },
  forgotPassword: {
    eyebrow: 'RESET',
    headline: 'Forgot your password?',
    body:
      'Enter the email tied to your account. If we recognise it, we’ll send a reset link within a minute.',
    emailLabel: 'WORK EMAIL',
    emailPlaceholder: 'florence.larrieu@acme.com',
    submitButton: 'Send reset link →',
    backLink: '← Back to sign-in',
    successHeadline: 'Check your inbox',
    successBodyTemplate:
      'If {email} is registered, a reset link is on its way. It’s valid for 30 minutes.',
    successResend: 'Didn’t get it? Try again',
    errorRateLimited: 'Too many attempts. Wait a moment, then try again.',
    errorGeneric: 'Couldn’t request a reset right now. Try again.',
  },
  passwordReset: {
    eyebrow: 'NEW PASSWORD',
    headline: 'Choose a new password',
    bodyTemplate: 'For {email}. This link is valid for 30 minutes.',
    codeLabel: 'CODE FROM EMAIL',
    codeHint: 'Paste the 6-digit code we sent you.',
    newPasswordLabel: 'NEW PASSWORD',
    confirmPasswordLabel: 'CONFIRM PASSWORD',
    submitButton: 'Update password →',
    errorCodeInvalid: 'That code doesn’t look right. Check your email and try again.',
    errorCodeExpired: 'That code has expired. Request a fresh reset link.',
    errorWeakPassword: 'That password doesn’t meet the policy requirements.',
    errorMismatch: 'Passwords don’t match.',
    errorGeneric: 'Couldn’t update your password. Try again.',
    successHeadline: 'Password updated',
    successBody: 'You can now sign in with your new password.',
    successButton: 'Continue to sign-in →',
  },
  emailVerify: {
    eyebrow: 'EMAIL VERIFICATION',
    headline: 'Check your inbox',
    bodyTemplate:
      'We sent a verification link to {email}. Click it from this device to activate your account.',
    waitingStatus: 'Waiting for confirmation…',
    waitingCaption: 'THIS SCREEN WILL UPDATE AUTOMATICALLY',
    resendPrefix: 'Didn’t get it?',
    resendCountdownTemplate: 'RESEND IN {seconds} S',
    resendButton: 'Resend',
    changeEmailLink: 'Change email address',
    verifyingStatus: 'Verifying…',
    successHeadline: 'Email verified',
    successBody: 'You can now sign in to GC Coach.',
    successButton: 'Continue to sign-in →',
    errorHeadline: 'Verification failed',
    errorBody:
      'This link is no longer valid. Request a new verification email or contact support.',
    errorRequestNew: 'Request a new link',
  },
  sessionExpired: {
    badge: 'SESSION EXPIRED',
    captionTemplate: '{minutes} MIN INACTIVE',
    headline: 'Sign back in to continue',
    body:
      'For your security, we ask you to re-authenticate after a period of inactivity.',
    accountLabel: 'ACCOUNT',
    passwordLabel: 'PASSWORD',
    submitButton: 'Resume session →',
    signOutLink: 'Sign out instead',
    saveCaption: 'YOUR WORK IS SAVED',
    errorWrongPassword: 'That password didn’t work. Try again.',
    errorRateLimited: 'Too many attempts. Wait a moment, then try again.',
    errorGeneric: 'Couldn’t reauthenticate. Try again.',
  },
  shell: {
    brandWordmark: 'GC Coach',
    brandSub: 'ADMIN · BACK OFFICE',
    sectionOverview: 'OVERVIEW',
    sectionKnowledge: 'KNOWLEDGE',
    sectionInsights: 'INSIGHTS',
    sectionCustomers: 'CUSTOMERS',
    sectionAdministration: 'ADMINISTRATION',
    navDashboard: 'Dashboard',
    navAnalytics: 'Usage analytics',
    navNotifications: 'Notifications & status',
    navSources: 'Source documents',
    navReview: 'Review queue',
    navKb: 'Knowledge base',
    navFrameworks: 'Frameworks',
    navPersonae: 'Personae',
    navBenchmark: 'Benchmark Engine',
    navRag: 'RAG pipeline',
    navLabs: 'GC Labs',
    navTenants: 'Tenants',
    navModeration: 'Moderation queue',
    navBilling: 'Billing & invoices',
    navUsers: 'Users & roles',
    navAudit: 'Audit log',
    navSettings: 'Settings',
    regionLabel: 'EU-WEST-1',
    notificationsAriaLabel: 'Notifications',
    userMenuMyProfile: 'My profile',
    userMenuPreferences: 'Preferences',
    userMenuShortcuts: 'Keyboard shortcuts',
    userMenuSignOut: 'Sign out',
    stateLoading: 'LOADING…',
    state401Title: 'Session expired',
    state401Body: 'Your session has expired. Sign back in to continue.',
    state401Button: 'Sign back in →',
    stateNetworkTitle: 'Connection lost',
    stateNetworkBody: 'Can’t reach the server. Check your connection — we’ll keep retrying.',
    stateNetworkButton: 'Retry',
  },
  roles: {
    super_admin: 'Super Admin',
    content_editor: 'Content Editor',
    customer_success: 'Customer Success',
    analyst: 'Analyst',
    read_only: 'Read-only',
  },
  roleDescriptions: {
    super_admin: 'Full access · billing · settings · user management',
    content_editor: 'KB · frameworks · publications',
    customer_success: 'Tenants · onboarding · support',
    analyst: 'Analytics · benchmark · export',
    read_only: 'Read-only access, audit',
  },
  permissionLabels: {
    'kb:read': 'View KB & Q&A',
    'kb:write': 'Edit KB & Q&A',
    'sources:write': 'Ingest source docs',
    'frameworks:write': 'Author frameworks',
    'analytics:read': 'View analytics',
    'benchmark:read': 'View benchmark',
    'benchmark:write': 'Edit benchmark data',
    'tenants:read': 'View tenants',
    'tenants:write': 'Manage tenants',
    'moderation:read': 'Moderation queue',
    'billing:write': 'Manage billing',
    'users:read': 'View team',
    'users:write': 'Manage team & roles',
    'settings:read': 'View system settings',
    'settings:write': 'System settings',
    'audit:read': 'View audit log',
  },
  permissionGroups: {
    knowledge: 'KNOWLEDGE',
    insights: 'INSIGHTS',
    customers: 'CUSTOMERS',
    admin: 'ADMINISTRATION',
  },
  users: {
    breadcrumbTeam: 'TEAM',
    breadcrumbUsers: 'USERS & ROLES',
    breadcrumbRoles: 'ROLES',
    title: 'Users & roles',
    subTemplate: 'The internal GC Coach team. {active} active · {invited} invited · {suspended} suspended.',
    manageRolesButton: 'Manage roles',
    inviteButton: '+ Invite user',
    statUsers: 'USERS',
    statActive: 'ACTIVE',
    statPending: 'PENDING INVITES',
    statMfa: 'MFA ENABLED',
    statMfaPendingTemplate: '{count} pending setup',
    chipAllTemplate: 'All ({n})',
    chipActiveTemplate: 'Active ({n})',
    chipInvitedTemplate: 'Invited ({n})',
    chipSuspendedTemplate: 'Suspended ({n})',
    chipRemovedTemplate: 'Removed ({n})',
    roleFilterLabel: 'ROLE',
    roleFilterAll: 'All',
    searchPlaceholder: 'Search users…',
    colUser: 'USER',
    colRole: 'ROLE',
    colRegion: 'REGION',
    colMfa: 'MFA',
    colLastSeen: 'LAST SEEN',
    colStatus: 'STATUS',
    mfaOn: '● ON',
    mfaOff: '○ —',
    statusActive: '● ACTIVE',
    statusInvited: '● INVITED',
    statusSuspended: '● SUSPENDED',
    statusRemoved: '○ REMOVED',
    invitedRowName: 'Invite sent',
    footerCountTemplate: '{n} USERS · NO PAGINATION (INTERNAL TEAM)',
    emptyTitle: 'No users match',
    emptyBody: 'Try clearing filters or invite a teammate.',
    // Invite drawer
    inviteTitle: 'Invite a user',
    inviteIntro:
      'The user gets an email invitation. On first sign-in they set a password, then enable MFA (required).',
    fieldEmail: 'WORK EMAIL',
    fieldEmailPlaceholderTemplate: 'name@{firstDomain}',
    fieldEmailHintTemplate: 'Allowed domains: {domains}',
    fieldFirstName: 'FIRST NAME',
    fieldLastName: 'LAST NAME',
    fieldRole: 'ROLE',
    fieldRegion: 'DATA REGION',
    regionEuW1: 'EU-W1 (Dublin)',
    permissionPreview: 'GRANTED PERMISSIONS',
    sendInvite: 'Send invitation',
    cancel: 'Cancel',
    inviteErrorEmailDomainTemplate: 'Email must use one of the allowed domains: {domains}.',
    inviteErrorEmailFormat: 'Enter a valid email address.',
    inviteErrorRequired: 'This field is required.',
    inviteErrorDuplicate: 'A user with this email already exists.',
    inviteErrorGeneric: 'Couldn’t send the invitation. Try again.',
    // Detail drawer
    detailMetaRole: 'ROLE',
    detailMetaRegion: 'REGION',
    detailMetaMfa: 'MFA',
    detailMetaTenure: 'TENURE',
    detailMetaLastSeen: 'LAST SEEN',
    detailMetaCreatedBy: 'CREATED BY',
    detailMfaEnabled: 'TOTP · enabled',
    detailMfaPending: 'TOTP · pending',
    detailMfaNone: '—',
    detailChangeRole: 'CHANGE ROLE',
    detailPermissions: 'PERMISSIONS',
    detailActiveSessions: 'ACTIVE SESSIONS',
    detailSessionsBody:
      'Per-session tracking lands later. For now you can revoke all sessions in one click.',
    detailSignOutAll: 'Sign out from all devices',
    detailSendReset: 'Send password reset',
    detailSendResetActiveOnly:
      'Password reset is only available for active users. Restore or reactivate first.',
    detailResetMfa: 'Reset MFA device',
    detailSuspend: 'Suspend access',
    detailReactivate: 'Reactivate access',
    detailRemove: 'Remove user',
    detailRestore: 'Restore user',
    detailRestoreSuperAdminOnly: 'Only Super Admins can restore a removed user.',
    detailRestoreForbidden:
      'Restore failed — your account doesn’t have super_admin permission.',
    detailConfirmRemove: 'Remove this user?',
    detailConfirmRemoveBody:
      'Removal is a permanent offboarding signal. The account is preserved for audit but hidden from the default view. They can’t sign in again unless a Super Admin restores them.',
    detailConfirmRemoveReasonLabel: 'Reason (optional)',
    detailConfirmRemoveReasonPlaceholder: 'e.g. left the company on 2026-06-30',
    detailConfirmRestore: 'Restore removed user',
    detailConfirmRestoreBody:
      'This user was previously removed. Restoring brings them back to Active and re-enables sign-in. The original removal stays in the audit log.',
    detailConfirmRestoreTypeLabel: 'Type RESTORE (in capitals) to confirm:',
    detailConfirmSuspend: 'Suspend this user?',
    detailConfirmSuspendBody: 'They will be signed out everywhere and can’t sign in again until reactivated.',
    detailConfirmYes: 'Confirm',
    detailConfirmCancel: 'Cancel',
    detailResendInvite: 'Resend invitation',
    detailGenericError: 'Something went wrong. Try again.',
    detailTenureNever: 'Never signed in',
    detailLastSeenNever: 'Never',
    // Manage roles page
    rolesTitle: 'Roles & permissions',
    rolesSub:
      '5 predefined roles. Permissions are enforced server-side (RBAC) — this grid is the reference.',
    rolesBackButton: '← Back to users',
    rolesCallout:
      'The 5 base roles are read-only (locked). Create a custom role to tune a permission combination. Custom roles are post-MVP.',
    rolesColUsersTemplate: '{n} USERS · LOCKED',
  },
  tenants: {
    breadcrumbCustomers: 'CUSTOMERS',
    breadcrumbTenants: 'TENANTS',
    title: 'Tenants',
    subTemplate: '{active} active · {suspended} suspended · {trial} trial.',
    newTenantButton: '+ New tenant',
    // Stat cards
    statTenants: 'TENANTS',
    statActive: 'ACTIVE',
    statTrial: 'TRIAL',
    statPastDue: 'PAST DUE',
    // Filters
    chipAllTemplate: 'All ({n})',
    chipActiveTemplate: 'Active ({n})',
    chipSuspendedTemplate: 'Suspended ({n})',
    chipRemovedTemplate: 'Removed ({n})',
    standingFilterLabel: 'STANDING',
    standingFilterAll: 'All',
    jurisdictionFilterLabel: 'JURISDICTION',
    jurisdictionFilterAll: 'All',
    searchPlaceholder: 'Search tenants…',
    // Columns
    colTenant: 'TENANT',
    colJurisdiction: 'JURISDICTION',
    colRegion: 'REGION',
    colPlan: 'PLAN',
    colStanding: 'STANDING',
    colUsers: 'USERS',
    colCreated: 'CREATED',
    colStatus: 'STATUS',
    // Status pills
    statusActive: '● ACTIVE',
    statusSuspended: '● SUSPENDED',
    statusRemoved: '○ REMOVED',
    // Standing pills
    standingTrial: 'TRIAL',
    standingActive: 'ACTIVE',
    standingPastDue: 'PAST DUE',
    standingChurned: 'CHURNED',
    footerCountTemplate: '{n} TENANTS · NO PAGINATION (V1)',
    // Empty states
    emptyTitle: 'No tenants match',
    emptyBody: 'Try clearing filters or create a new tenant.',
    emptyMarketingTitle: 'No tenants yet',
    emptyMarketingBody:
      'Create the first customer organization to start provisioning GC Coach.',
    // Wizard
    wizardTitleStep1: 'New tenant — Step 1 of 2 · Details',
    wizardTitleStep2: 'New tenant — Step 2 of 2 · First administrator',
    wizardNext: 'Next →',
    wizardBack: '← Back',
    wizardCreate: 'Create tenant',
    wizardSkipAndCreate: 'Skip & create',
    cancel: 'Cancel',
    wizardDeferralTitle: 'V1 deferral',
    wizardDeferralBody:
      'In V1, tenants are created but tenant administrators are not yet invited. Provide these details now to pre-populate the invite once the tenant portal launches in Phase 1. Skip this step to create the tenant alone.',
    fieldName: 'ORGANIZATION NAME',
    fieldNamePlaceholder: 'Acme Legal Department',
    fieldSlug: 'SLUG',
    fieldSlugPlaceholder: 'acme-legal',
    fieldSlugHint: 'Auto-derived from name; immutable after create.',
    fieldJurisdiction: 'JURISDICTION',
    fieldRegion: 'DATA REGION',
    regionEuW1: 'EU-W1 (Dublin)',
    regionUsE1: 'US-E1 (N. Virginia)',
    regionApSe1: 'AP-SE1 (Singapore)',
    regionEnterpriseTier: 'Enterprise tier',
    fieldStanding: 'STANDING',
    standingTrialLabel: 'Trial — pre-paid evaluation',
    standingTrialDesc: 'Default for new tenants; convert to Active when paid.',
    standingActiveLabel: 'Active — paying customer',
    standingActiveDesc: 'Tenant has a paid subscription.',
    addBillingDetails: 'Add billing details ▾',
    fieldBillingEmail: 'BILLING EMAIL',
    fieldPlanTier: 'PLAN TIER',
    fieldSeatCount: 'SEAT COUNT',
    fieldAdminEmail: 'EMAIL',
    fieldAdminEmailHint: 'Must not be a @gc-coach.ai address',
    fieldAdminFirstName: 'FIRST NAME',
    fieldAdminLastName: 'LAST NAME',
    fieldAdminRole: 'ROLE',
    adminRoleChipLabel: 'TENANT ADMIN',
    // Field errors
    fieldErrorRequired: 'Required',
    fieldErrorEmailFormat: 'Invalid email',
    fieldErrorInternalDomain:
      'Cannot use @gc-coach.ai (internal-staff domain)',
    fieldErrorInvalidNumber: 'Must be a positive whole number',
    fieldErrorDuplicateName: 'A tenant with this name already exists',
    createGenericError: 'Could not create the tenant. Try again.',
    // Detail drawer
    detailMetaJurisdiction: 'JURISDICTION',
    detailMetaRegion: 'REGION',
    detailMetaStanding: 'STANDING',
    detailMetaUsers: 'USERS',
    detailMetaCreatedBy: 'CREATED BY',
    detailMetaCreatedAt: 'CREATED',
    detailSlug: 'SLUG',
    detailSlugPermanent: 'Identifier is permanent.',
    detailEditMetadata: 'EDIT METADATA',
    detailSaveMetadata: 'Save changes',
    detailChangeStanding: 'CHANGE STANDING',
    detailTenantUsers: 'TENANT USERS',
    detailTenantUsersEmpty:
      'No tenant users yet. Tenant admin invites land in Phase 1 when the tenant portal launches.',
    detailTenantUsersHint:
      'Invite, suspend, and manage this tenant’s users on the dedicated page.',
    detailTenantUsersManage: 'Manage users →',
    detailRemovalMeta: 'REMOVAL',
    detailRemovedAtTemplate: 'Removed {when} by {by}',
    detailSuspend: 'Suspend access',
    detailReactivate: 'Reactivate access',
    detailRemove: 'Remove tenant',
    detailRestore: 'Restore tenant',
    detailRestoreSuperAdminOnly:
      'Only Super Admins can restore a removed tenant.',
    detailRestoreForbidden:
      'Restore failed — your account doesn’t have super_admin permission.',
    detailConfirmSuspend: 'Suspend this tenant?',
    detailConfirmSuspendBody:
      'All tenant users will be blocked from signing in until the tenant is reactivated.',
    detailConfirmRemove: 'Remove this tenant?',
    detailConfirmRemoveBody:
      'Removal is a permanent offboarding signal. The tenant record is preserved for audit but hidden from the default view. Tenant users will be anonymized per our consumer-pool retention policy. The tenant can be restored only by a Super Admin.',
    detailConfirmRemoveReasonLabel: 'Reason (optional)',
    detailConfirmRemoveReasonPlaceholder: 'e.g. contract ended on 2026-12-31',
    detailConfirmRestore: 'Restore removed tenant?',
    detailConfirmRestoreBody:
      'This tenant was previously removed. Restoring brings it back to Active and re-enables tenant operations. The original removal stays in the audit log.',
    detailConfirmRestoreTypeLabel: 'Type RESTORE (in capitals) to confirm:',
    detailConfirmYes: 'Confirm',
    detailConfirmCancel: 'Cancel',
    detailGenericError: 'Something went wrong. Try again.',
  },
  tenantUsers: {
    // Page chrome
    breadcrumbUsers: 'USERS',
    titleTemplate: '{name} · Users',
    titleFallback: 'Tenant users',
    subTemplate: '{active} active · {invited} invited · {suspended} suspended.',
    backToTenant: '← Back to tenant',
    inviteButton: '+ Invite user',
    tenantNotFound: 'Tenant not found.',
    // Stat cards
    statUsers: 'USERS',
    statActive: 'ACTIVE',
    statInvited: 'INVITED',
    statSuspended: 'SUSPENDED',
    // Filter chips
    chipGroupLabel: 'Status filter',
    chipAllTemplate: 'All ({n})',
    chipActiveTemplate: 'Active ({n})',
    chipInvitedTemplate: 'Invited ({n})',
    chipSuspendedTemplate: 'Suspended ({n})',
    chipRemovedTemplate: 'Removed ({n})',
    chipAnonymizedTemplate: 'Anonymized ({n})',
    searchPlaceholder: 'Search by email or name…',
    // Columns
    colUser: 'USER',
    colRole: 'ROLE',
    colLastSeen: 'LAST SEEN',
    colStatus: 'STATUS',
    // Status pills
    statusActive: '● ACTIVE',
    statusInvited: '● INVITED',
    statusSuspended: '● SUSPENDED',
    statusRemoved: '○ REMOVED',
    statusAnonymized: '○ ANONYMIZED',
    // Role
    roleTenantAdmin: 'Tenant admin',
    roleTenantAdminLocked: 'Tenant admin (V1 default — fixed)',
    // Row helpers
    invitedRowName: '(invitation pending)',
    // Empty + footer
    emptyTitle: 'No tenant users match',
    emptyBody: 'Try clearing filters or invite the first user.',
    footerCountTemplate: '{n} USERS · NO PAGINATION (V1)',
    // Invite drawer
    inviteTitle: 'Invite a tenant user',
    inviteIntro:
      'The invited user will receive an email with sign-in instructions. They join this tenant with the Tenant Admin role.',
    fieldEmail: 'EMAIL',
    fieldEmailPlaceholder: 'name@company.com',
    fieldFirstName: 'FIRST NAME',
    fieldLastName: 'LAST NAME',
    fieldRole: 'ROLE',
    sendInvite: 'Send invitation',
    cancel: 'Cancel',
    inviteErrorRequired: 'Required',
    inviteErrorEmailFormat: 'Enter a valid email address',
    inviteErrorDuplicate: 'A tenant user with this email already exists.',
    inviteErrorTenantInactive:
      'This tenant is not active. Reactivate it before inviting users.',
    inviteErrorGeneric: 'Could not send invitation. Please try again.',
    // Detail drawer — meta + actions
    detailMetaRole: 'ROLE',
    detailMetaLocale: 'LOCALE',
    detailMetaMfa: 'MFA',
    detailMetaTenure: 'TENURE',
    detailMetaLastSeen: 'LAST SEEN',
    detailMetaCreatedBy: 'INVITED BY',
    detailMfaEnabled: 'Enabled',
    detailMfaPending: 'Pending',
    detailMfaNone: 'None',
    detailLastSeenNever: 'Never signed in',
    detailResendInvite: 'Resend invitation',
    detailSuspend: 'Suspend access',
    detailReactivate: 'Reactivate access',
    detailRemove: 'Remove user',
    detailAnonymize: 'Anonymize (GDPR)',
    detailAnonymizedNote: 'This user has been anonymized. No further actions are available.',
    detailAnonymizeSuperAdminOnly: 'Only Super Admins can anonymize a removed user.',
    detailAnonymizeForbidden:
      'Anonymize failed — your account doesn’t have super_admin permission.',
    // Confirm panels
    detailConfirmSuspend: 'Suspend this user?',
    detailConfirmSuspendBody:
      'The user will be blocked from signing in until you reactivate. Their active sessions will be terminated immediately.',
    detailConfirmRemove: 'Remove this user?',
    detailConfirmRemoveBody:
      'Removal is a soft offboarding signal. The user is hidden from the default view and blocked from signing in. The record is preserved for audit. A Super Admin can later anonymize (GDPR-erase) the record.',
    detailConfirmRemoveReasonLabel: 'Reason (optional)',
    detailConfirmRemoveReasonPlaceholder: 'e.g. left the company on 2026-12-31',
    detailConfirmAnonymize: 'Anonymize this user? This is permanent.',
    detailConfirmAnonymizeBody:
      'GDPR erasure overwrites email and name with sentinel values and locks the Cognito account. The record stays for foreign-key integrity but carries no PII. This cannot be undone.',
    detailConfirmAnonymizeTypeLabel: 'Type ANONYMIZE (in capitals) to confirm:',
    detailConfirmYes: 'Confirm',
    detailConfirmCancel: 'Cancel',
    detailGenericError: 'Something went wrong. Try again.',
  },
  frameworks: {
    // Breadcrumbs (shared by View 7 + 8)
    breadcrumbKnowledge: 'KNOWLEDGE',
    breadcrumbFrameworks: 'FRAMEWORKS',
    // ─── View 8 · Library Management (/admin/knowledge/frameworks) ──────
    libraryTitle: 'Frameworks library',
    librarySubTemplate:
      '{total} frameworks · {published} published · {draft} draft · {retired} retired.',
    newFrameworkButton: '+ New framework',
    // Stat cards
    statTotal: 'TOTAL',
    statPublished: 'PUBLISHED',
    statDraft: 'DRAFT',
    statRetired: 'RETIRED',
    // Filter chips
    chipAllTemplate: 'All ({n})',
    chipDraftTemplate: 'Draft ({n})',
    chipPublishedTemplate: 'Published ({n})',
    chipRetiredTemplate: 'Retired ({n})',
    domainFilterLabel: 'DOMAIN',
    domainFilterAll: 'All',
    searchPlaceholder: 'Search frameworks…',
    // Columns
    colTitle: 'TITLE',
    colDomain: 'DOMAIN',
    colStatus: 'STATUS',
    colVersion: 'VERSION',
    colActivations: 'ACTIVATIONS',
    colLastEdited: 'LAST EDITED',
    colEditedBy: 'EDITED BY',
    // Editorial status pills (matches designer strings spec §Framework lifecycle)
    statusDraft: '● DRAFT',
    statusPublished: '● PUBLISHED',
    statusRetired: '○ RETIRED',
    footerCountTemplate: '{n} FRAMEWORKS · NO PAGINATION (V1)',
    // Empty states
    emptyTitle: 'No frameworks match',
    emptyBody: 'Try clearing filters or create a new framework.',
    emptyMarketingTitle: 'No frameworks yet',
    emptyMarketingBody:
      'Author the first best-practice path for one of the 8 knowledge domains.',
    // Loading / error
    loading: 'Loading frameworks…',
    listLoadError: 'Couldn’t load frameworks. Try again.',
    // ─── View 7 · Framework Editor (/admin/knowledge/frameworks/:id) ────
    editorBreadcrumbFrameworkPlaceholder: 'NEW FRAMEWORK',
    editorTitlePlaceholder: 'Framework title',
    editorSaveDraft: 'Save draft',
    editorPublish: 'Publish',
    editorRepublish: 'Republish',
    editorRetire: 'Retire',
    editorSaving: 'Saving…',
    editorSaved: 'Saved',
    // Section headers
    sectionMeta: 'Framework details',
    sectionMaturityTarget: 'Maturity target',
    sectionDeliverables: 'Deliverables',
    sectionPhases: 'Phases & activities',
    // Maturity Target — the framework's outcome contract
    maturityTargetDomainLabel: 'DOMAIN',
    maturityTargetDomainPlaceholder: 'Select domain…',
    maturityTargetLevelLabel: 'LEVEL',
    maturityTargetLevelPlaceholder: 'Select level…',
    maturityTargetHint:
      "Completing this framework advances the tenant's maturity in the selected domain to this level (evidence-based — the tenant confirms the shift on the completion screen).",
    maturityLevel: {
      initial: 'Initial',
      developing: 'Developing',
      established: 'Established',
      advanced: 'Advanced',
      optimized: 'Optimized',
    },
    // Meta fields
    fieldTitle: 'TITLE',
    fieldDomain: 'DOMAIN',
    fieldShortDescription: 'SHORT DESCRIPTION',
    fieldShortDescriptionHint: 'One-line summary for the library cards.',
    fieldLongDescription: 'LONG DESCRIPTION',
    fieldEstimatedEffort: 'ESTIMATED EFFORT',
    fieldEstimatedEffortPlaceholder: 'e.g. 8 weeks · 6 phases · ~25 activities',
    fieldTags: 'TAGS',
    fieldTagsPlaceholder: 'Add tag and press Enter…',
    // Deliverables authoring
    deliverablesIntro:
      'The tangible outputs the tenant has at the end of running this framework. Producing each deliverable is the point of the framework.',
    deliverablesEmptyPrompt:
      'What tangible outputs does the tenant have at the end of running this framework?',
    deliverablesEmptyExamples:
      'e.g. AI Governance Policy · Risk Register · Board Deck',
    deliverableSectionLabel: 'DELIVERABLE',
    addDeliverableButton: '+ Add deliverable',
    removeDeliverableButton: 'Remove deliverable',
    deliverableNameLabel: 'NAME',
    deliverableNamePlaceholder: 'e.g. AI Governance Policy',
    deliverableDescriptionLabel: 'DESCRIPTION',
    deliverableRubricLabel: 'EVALUATION RUBRIC (OPTIONAL)',
    deliverableRubricPlaceholder:
      'What "passes" looks like — the criteria a submitted deliverable must meet.',
    deliverableRubricHint:
      'Optional. Used by the coach evaluation loop (V1.x). Leave blank for self-report path.',
    // Persistent Deliverables rail
    railDeliverablesTitle: 'DELIVERABLES',
    railEmpty: 'None declared yet.',
    railUnnamed: 'Untitled deliverable',
    railNoTemplateYet: 'No template yet',
    // Phase authoring
    phaseSectionLabel: 'PHASE',
    addPhaseButton: '+ Add phase',
    removePhaseButton: 'Remove phase',
    phaseTitleLabel: 'TITLE',
    phaseDescriptionLabel: 'DESCRIPTION',
    phaseTagsLabel: 'TAGS',
    // Phase-level Documents. Role locked to `reference` (deliverable
    // templates live on activities).
    phaseDocumentsLabel: 'PHASE REFERENCE DOCUMENTS',
    phaseDocumentsEmpty:
      'No reference documents on this phase — checklists, guides, or examples the tenant reads before running activities.',
    addPhaseDocumentButton: '+ Add document',
    removePhaseDocumentButton: 'Remove document',
    // Document source selector (shared with activity documents in MR 6)
    docSourceKbEntry: 'KB entry',
    docSourceUpload: 'Uploaded file',
    docTitlePlaceholder: 'Document title (shown to the tenant)',
    docKbIdPlaceholder: 'KB entry id',
    docFileRefPlaceholder: 'Uploaded file ref',
    // Activity authoring
    activitySectionLabel: 'ACTIVITY',
    addActivityButton: '+ Add activity',
    removeActivityButton: 'Remove activity',
    activityTitleLabel: 'TITLE',
    activityDescriptionLabel: 'DESCRIPTION',
    activityTagsLabel: 'TAGS',
    // Activity v2 authoring
    activityUntitled: 'Untitled activity',
    activityDurationLabel: 'DURATION',
    activityDurationPlaceholder: 'Select duration…',
    activityDurationOverrideLabel: 'TENANT OVERRIDE',
    activityDurationOverrideDescription:
      'Tenant can shorten or lengthen at activation.',
    durationBucket: {
      lt_1_month: 'Under 1 month',
      '1_3_months': '1–3 months',
      '3_6_months': '3–6 months',
      '6_9_months': '6–9 months',
      '9_12_months': '9–12 months',
      '12_18_months': '12–18 months',
      '18_24_months': '18–24 months',
    },
    activityDependenciesLabel: 'DEPENDENCIES',
    activityDependenciesEmpty: 'This is the only activity — nothing to depend on yet.',
    activityDependenciesHint:
      'Cross-phase allowed. Runtime is a soft lock with preview.',
    activityStakeholdersLabel: 'STAKEHOLDERS',
    activityStakeholdersPlaceholder: 'Add stakeholder and press Enter…',
    activityDocumentsLabel: 'DOCUMENTS',
    activityDocumentsEmpty:
      'No documents on this activity — reference guides or deliverable templates.',
    addActivityDocumentButton: '+ Add document',
    removeActivityDocumentButton: 'Remove document',
    docSourceLabel: 'Source',
    docRoleLabel: 'Role',
    docRoleReference: 'Reference',
    docRoleDeliverableTemplate: 'Deliverable template',
    docProducesLabel: 'PRODUCES DELIVERABLES',
    docProducesNoDeliverables:
      'No deliverables declared yet. Add one in the Deliverables section above so this template can claim it.',
    docProducesHint: 'A template can feed several deliverables.',
    railTemplateClaimed: 'Template claimed',
    // Lifecycle confirm panels
    publishConfirmTitle: 'Publish this framework?',
    publishConfirmBody:
      'Tenants will see it in the library and can activate it. You can retire later — existing instances continue to completion.',
    publishConfirmYes: 'Publish',
    publishConfirmCancel: 'Cancel',
    retireConfirmTitle: 'Retire this framework?',
    retireConfirmBody:
      'It will no longer be available for new activations. Tenants already running it keep their instances. You can republish later.',
    retireConfirmYes: 'Retire',
    retireConfirmCancel: 'Cancel',
    republishConfirmTitle: 'Republish this framework?',
    republishConfirmBody:
      'It becomes activatable again for new tenants. A new version is recorded; existing citations stay frozen to their original version.',
    republishConfirmYes: 'Republish',
    republishConfirmCancel: 'Cancel',
    // Validation surface
    publishBlockedTitle: 'Not ready to publish',
    publishBlockedMaturityTargetRequired: 'Set a maturity target (domain + level).',
    publishBlockedNoDeliverables: 'Declare at least one deliverable.',
    publishBlockedDeliverableUnnamed: 'Every deliverable needs a name.',
    publishBlockedDeliverableUnclaimed:
      'Every deliverable must be claimed by at least one activity deliverable template.',
    publishBlockedAtLeastOnePhase: 'At least one phase is required.',
    publishBlockedPhaseNeedsActivity: 'Every phase needs at least one activity.',
    publishBlockedActivityDurationMissing: 'Every activity needs a duration bucket.',
    fieldErrorRequired: 'Required',
    detailGenericError: 'Something went wrong. Try again.',
    detailNotFound: 'Framework not found.',
  },
  personae: {
    breadcrumbKnowledge: 'KNOWLEDGE',
    breadcrumbPersonae: 'PERSONAE',
    listTitle: 'Personae',
    newPersonaButton: '+ New persona',
    searchPlaceholder: 'Search personae…',
    statTotal: 'PERSONAE',
    statActive: 'ACTIVE',
    statScenariosRun: 'SCENARIOS RUN',
    statResponsesReviewed: 'RESPONSES REVIEWED',
    filterAll: 'All',
    filterActive: 'Active',
    filterArchived: 'Archived',
    archivedBadge: 'Archived',
    colName: 'Name',
    colSector: 'Industry',
    colSize: 'Size',
    colJurisdiction: 'Jurisdiction',
    colScenarios: 'Scenarios',
    colUpdated: 'Updated',
    emptyTitle: 'No personae yet',
    emptyBody: 'Create your first test profile to start evaluating the AI Coach.',
    editorCreateTitle: 'New persona',
    editorCreateSub: 'Define a fictitious company + GC profile to test the AI Coach.',
    editorEditSub: 'Edit persona attributes.',
    editorLoadError: 'Failed to load persona.',
    editorSaveError: 'Failed to save persona.',
    cancelButton: 'Cancel',
    saveButton: 'Save persona',
    savingButton: 'Saving…',
    groupIdentity: 'Identity',
    groupGeography: 'Geography & jurisdiction',
    groupGCProfile: 'GC profile',
    groupMaturity: 'Maturity per domain',
    groupBusinessContext: 'Business context',
    fieldName: 'Persona name',
    fieldNamePlaceholder: 'Northgate Legal',
    fieldSector: 'Sector',
    fieldRevenueBand: 'Revenue band',
    fieldEmployeeBand: 'Employee band',
    fieldLegalDeptSize: 'Legal department size',
    fieldGeographies: 'Operating geographies (ISO codes, comma-separated)',
    fieldPrimaryJurisdiction: 'Primary jurisdiction',
    fieldYearsExperience: 'Years of experience',
    fieldBackground: 'Career background',
    background_in_house: 'In-house',
    background_private_practice: 'Private practice',
    background_mixed: 'Mixed',
    background_academic: 'Academic',
    background_other: 'Other',
    maturityHelper: 'Set all 8 before saving.',
    maturity_initial: 'Initial',
    maturity_developing: 'Developing',
    maturity_established: 'Established',
    maturity_advanced: 'Advanced',
    maturity_optimized: 'Optimized',
    domain_D1_strategy_org: 'D1 Strategy & Organisation',
    domain_D2_external_counsel: 'D2 External Counsel',
    domain_D3_legal_ops_tech: 'D3 Legal Ops & Tech',
    domain_D4_team_dev: 'D4 Team Development',
    domain_D5_kpis_performance: 'D5 KPIs & Performance',
    domain_D6_budget_finance: 'D6 Budget & Finance',
    domain_D7_stakeholder_comms: 'D7 Stakeholder Communication',
    domain_D8_governance_risk: 'D8 Governance & Risk',
    contextHelper:
      'Describe the company’s current situation — recent events, regulatory pressures, strategic priorities. This is injected verbatim into the Coach’s prompt.',
    contextPlaceholder:
      'The company just completed a cross-border acquisition and is under new regulatory scrutiny…',
    detailLoadError: 'Failed to load persona.',
    detailActiveSub: 'Active test persona.',
    detailArchivedSub: 'Archived. Restore to re-enable Labs testing.',
    editButton: 'Edit persona',
    archiveButton: 'Archive',
    archiveError: 'Failed to archive persona.',
    restoreButton: 'Restore',
    restoreError: 'Failed to restore persona.',
    restoreSuperAdminOnly: 'Super Admin only',
    openInLabsButton: 'Open in Labs →',
    noContext: 'No business context set.',
    testScenariosTitle: 'Test scenarios',
    noScenarios: 'No test scenarios yet. Open in Labs to start one.',
  },
  mapReview: {
    // Shell keys only — MR 9 folds in the full EN/FR string surface
    // from design/UI : UX v1.0/for-engineering-ingest-review/specs/
    // map-review-strings.md (per-action toolbars, gap-block copy,
    // reject modal, save-as-draft, boundary-issue affordances, etc.).
    breadcrumbKnowledge: 'KNOWLEDGE',
    breadcrumbSources: 'SOURCE DOCUMENTS',
    breadcrumbMapReview: 'MAP REVIEW',
    loadingTitle: 'Loading map…',
    loadingSub: 'Fetching source text + resolved chapter structure.',
    loadErrorTitle: 'Could not load map',
    loadErrorGeneric: 'The map could not be loaded. Try again in a moment.',
    proposingSub: 'The AI proposer is still analyzing this document. Reload in ~60s.',
    // Meta line pieces
    metaChapterLabel: 'CHAPTER',
    metaOf: 'OF',
    metaWords: 'WORDS',
    metaTokens: 'TOK',
    // Status pills
    statusAwaiting: 'AWAITING REVIEW',
    statusApproved: 'APPROVED · READ-ONLY',
    statusRejected: 'REJECTED',
    // Footer
    footerReject: 'Reject',
    footerSave: 'Save',
    footerSaving: 'Saving…',
    footerApprove: 'Approve & ingest',
    footerApproving: 'Approving…',
    footerApproveDirtyTooltip: 'Save your changes first, then approve.',
    footerReadOnly: 'APPROVED — read-only',
    footerDirty: 'UNSAVED CHANGES',
    footerApproveWithUnsavedTemplate: 'Approving saves your {n} edits first',
    // Unsaved-changes bar (source-map MR 6) — dark, sticky between the
    // doc header and the panes when there are pending edits.
    unsavedBarCountTemplate: '{n} UNSAVED EDITS',
    unsavedBarDescription:
      'Edits are local until you save. Approving also saves.',
    unsavedBarDiscardAll: 'Discard all',
    unsavedBarSave: 'Save',
    unsavedBarSaveShortcut: '⌘S',
    // Discard-all confirm (MR 6)
    discardModalTitleTemplate: 'Discard {n} unsaved edits?',
    discardModalBody:
      'The map returns to its last saved state. Saved versions are unaffected.',
    discardModalCancel: 'Cancel',
    discardModalConfirm: 'Discard edits',
    // Save toast (MR 6 — pattern setup; MR 8/10 fire more)
    toastSavedTemplate: 'Saved {n} edits',
    // Selection toolbar (source-map MR 7) — appears when the reviewer
    // drag-selects text inside the current section. Split is the primary
    // idiom; Move-to-prev / Move-to-next adjust neighbouring boundaries.
    // Rewrite / Insert / Exclude are disabled placeholders until MR 7b.
    selectionToolbarAria: 'Selection actions',
    selectionCountTemplate: '{p} PARAGRAPHS · {w} WORDS',
    selectionSplitHere: 'Split here',
    selectionSplitFirstParaBlocked:
      'Selection is already at the top of this section — splitting here would produce an empty top half.',
    selectionSplitHereTagTemplate: 'SPLIT HERE · ~{before} / ~{after} tok',
    selectionMoveToTemplate: 'Move to {ref}',
    selectionRewrite: 'Rewrite',
    selectionInsertAfter: 'Insert after',
    selectionExclude: 'Exclude',
    selectionActionComingSoon:
      'Paragraph-content actions (Rewrite / Insert / Exclude) land in MR 7b with the paragraph-overrides schema.',
    // Toast copy for split / move
    toastSplitTemplate: '{ref} split — new section added',
    toastMoveTemplate: 'Range moved to {ref}',
    // ChunkSizeBadge — live token feedback (MR 9)
    chunkWas: 'was',
    chunkVeryShort: 'very short',
    footerRejected: 'REJECTED',
    footerRepropose: 'Re-propose map',
    footerReproposing: 'Re-proposing…',
    // Reject-with-reason modal (MR 8)
    rejectModalTitle: 'Reject this map?',
    rejectModalBody:
      'The proposal will be marked rejected. You can re-propose from scratch afterward. A reason helps future reviewers understand what was wrong.',
    rejectModalReasonPlaceholder: 'Optional reason (e.g., "Wrong chapter boundaries in Ch. 3–5")',
    rejectModalCancel: 'Cancel',
    rejectModalConfirm: 'Reject map',
    rejectModalConfirming: 'Rejecting…',
    // Placeholder pane copy (removed in MRs 4 + 5)
    leftPanePlaceholderTitle: 'STRUCTURE TREE',
    leftPanePlaceholderBody: 'The tree editor lands in the next MR of this build.',
    rightPanePlaceholderTitle: 'SOURCE TEXT',
    rightPanePlaceholderBody:
      'The right-hand pane will render the source text with each section highlighted so you can verify boundaries and spot gaps.',
    // MapTree — dense-row layout (source-map restructure MR 4)
    treeChapterPrefix: 'Chapter',
    treeSubSectionPrefix: 'Sub-section',
    treeChapterPrefixUpper: 'CHAPTER',
    treeSubSectionPrefixUpper: 'SUB-SECTION',
    treeChapterTitleLabel: 'CHAPTER TITLE',
    treeChapterTitlePlaceholder: 'Chapter title as it appears in the doc…',
    treeSubSectionLabelPlaceholder: 'Sub-section label…',
    treePatternDisclosureLabel: 'Edit match pattern',
    treePatternPlaceholder: 'Literal heading text (first 4-8 words)…',
    treePatternHint:
      'Anchor text used to locate this heading in the source. Case-insensitive; whitespace tolerant. Must be unique within the chapter.',
    treeAddSubSection: 'Add sub-section',
    treeDeleteSubSection: 'Delete sub-section',
    treePrevChapter: 'Previous chapter',
    treeNextChapter: 'Next chapter',
    treeNoMatchPill: 'NO MATCH',
    treeNoMatchTooltip:
      'Header text could not be located in the extracted source. Edit the pattern or check the source text on the right.',
    treeExcludedPill: '⊘ NOT INGESTED',
    treeExcludedTooltip:
      'This sub-section is marked to be left out of ingest. The chunker will skip it; no KB entry is produced.',
    // Edit-status chips on tree rows
    editTagEdited: 'EDITED',
    editTagNew: 'NEW',
    editTagEditing: 'EDITING',
    // Row action cluster. MR 8 reintroduces Split + Merge up now that
    // the backend paragraph_overrides data model is live.
    // Legacy row-cluster keys kept for the removal transition — the
    // cluster was removed from MapTree on 2026-10-21 (actions moved to
    // the right-pane SectionFloatingMenu). Delete these keys in a
    // follow-up MR once nothing references them.
    rowActionsAria: 'Sub-section actions',
    rowActionSplit: 'Split',
    rowActionSplitHint:
      'Focus this section — pick the exact split point by selecting text in the doc on the right.',
    rowActionMergeUp: 'Merge up',
    rowActionRename: 'Rename',
    rowActionDelete: 'Delete',
    // Right-pane section-tier menu (replaces the left-pane row cluster).
    sectionMenuAria: 'Section actions',
    sectionMenuSplit: '✂ SPLIT',
    sectionMenuMergeUp: '↑ Merge up',
    sectionMenuDelete: '× Delete',
    // Split redesign (2026-10-26 handoff: caret + preview) — section
    // enters an "armed" state via Split; a blinking caret follows the
    // mouse between words, text after the caret tints to preview the
    // new section, a sticky card shows the result with an editable
    // title and the derived anchor phrase. Carve case (3 sections) is
    // a second cut placed below the first. Strings mirror
    // design/UI : UX v1.0/for-engineering-split-new/specs/
    // split-strings.md verbatim.
    splitBanner: 'Click between two words where the new section should begin.',
    splitCancel: 'Cancel · Esc',
    splitHintCut: '✂ click to cut',
    splitHintSentence: '✂ new sentence',
    splitGripNew: '✂ NEW SECTION',
    splitGripResume: '✂ {parent} RESUMES',
    splitCardSplit: 'SPLIT · 2 SECTIONS',
    splitCardCarve: 'CARVE · 3 SECTIONS',
    splitCardDrag: 'drag the cut to adjust',
    splitKeeps: 'KEEPS',
    splitNew: 'NEW SECTION',
    splitResumes: 'RESUMES {parent}',
    splitResumesTitle: '{parent} (cont.) · {firstWords}…',
    splitStartsAt: 'STARTS AT',
    splitResumesAt: '{parent} RESUMES AT',
    splitUnique: '✓ UNIQUE',
    splitExtended: 'EXTENDED TO {n} WORDS',
    splitExtendedBody:
      'The first five words also appear earlier in this chapter, so the anchor runs on until it’s unique. Nothing to fix — move the cut if you’d rather start somewhere distinctive.',
    splitCreate: 'Create section',
    splitCreateCarve: 'Create 2 sections',
    splitEndFurther: '+ End it further down',
    splitEndFurtherPrompt:
      'Click where the new section ends and {parent} picks up again.',
    splitRemoveEnd: 'Remove end point',
    splitPreviewTag: 'PREVIEW',
    splitNewTag: 'NEW',
    splitSeam: '✂ SPLIT HERE · JUST NOW',
    splitToast: '{parent} split into two sections',
    splitToastCarve: '{parent} carved into three sections',
    splitUndo: 'UNDO',
    // Toast templates for the new MR 8 actions.
    toastRewriteTemplate: '{ref} rewritten — original kept in history',
    toastInsertTemplate: '{ref} authored block inserted',
    toastExcludeParagraphTemplate: '{ref} paragraph excluded from ingest',
    toastMergeUpTemplate: '{ref} merged into the section above',
    // Inline paragraph editor (Rewrite / Insert) — opened from the
    // selection toolbar when the reviewer picks one of those two.
    rewriteInsertPlaceholder:
      'Add framing or keywords to improve embedding…',
    rewriteSave: 'Save paragraph',
    rewriteCancel: 'Cancel',
    rewriteOriginalKept: 'Original kept in history',
    rewriteAriaRewrite: 'Rewrite paragraph',
    rewriteAriaInsert: 'Insert paragraph',
    // Boundary drag — label surfaces on hover of the section boundary
    // rule in the right pane; drag moves paragraphs between neighbours.
    boundaryDragLabel: '⇕ Drag to move this boundary',
    // MR 10 — inline UNDO on toast + the history drawer.
    toastUndoLabel: 'Undo',
    toastUndoShortcut: '⌘Z',
    historyToggleLabel: 'Edit history',
    historyToggleAria: 'Open the edit history drawer',
    historyDrawerTitle: 'Edit history',
    historyDrawerCloseAria: 'Close the history drawer',
    historyDrawerLoading: 'Loading history…',
    historyDrawerEmpty: 'No edits have been made to this map yet.',
    // Op labels — keys mirror BookStructureAuditOp (dashes → underscores
    // because i18n keys can't carry dashes in nested paths).
    historyOp_create: 'Created',
    historyOp_patch: 'Edited',
    historyOp_propose_complete: 'Propose-map completed',
    historyOp_propose_reset: 'Propose-map re-run',
    historyOp_approve: 'Approved',
    historyOp_reject: 'Rejected',
    // Synthetic actor labels for system-driven writes.
    historyActor_system_propose_worker: 'Propose worker',
    historyActor_system_propose_worker_failure: 'Propose worker (failed)',
    historyActor_system_propose_invoke_failure: 'System (invoke failure)',
    // Sticky reading header on the right pane (orientation MR 5).
    // Labels the section currently under the reader's eye.
    readingLabel: 'READING',
    readingHint: 'J / K to move',
    // Focused-section reading model (source-map MR 5b)
    docPanePrecedingSection: 'preceding section',
    docPaneFollowingSection: 'following section',
    docPaneSubNoMatchBody:
      'This sub-section’s header could not be located in the source text. Edit its match pattern on the left to point it at the real heading.',
    docPaneSubEmptyBody:
      'No body text between this heading and the next.',
    // SourceDocPane (MR 5) —
    docPaneChapterNoMatchTitle: 'Chapter header not found in the source',
    docPaneChapterNoMatchBody:
      'The chapter\'s match pattern didn\'t locate a heading in the extracted text. Edit the pattern on the left to point to the actual heading, or check whether the source text extracted correctly.',
    // SourceDocPane interactions (MR 6) —
    docPaneGapActionsAria: 'Gap actions',
    docPaneSectionActionsAria: 'Section actions',
    gapActionNewSection: '+ New section from this',
    gapActionExtendPrevious: '⊕ Extend previous section',
    gapActionMarkIntentional: '⊘ Leave out of ingest',
    sectionActionExclude: '⊘ Exclude from ingest',
    sectionActionUnexclude: '✓ Include in ingest',
    sectionActionDelete: '× Delete sub-section',
    // Selection toolbar (MR 7)
    selectionActionNewSection: '+ New section from selection',
    // No-match strips (MR 8b)
    docPaneNoMatchStripLabel: 'NO MATCH',
    docPaneNoMatchStripSingle: 'Expected sub-section header here',
    docPaneNoMatchStripMulti: 'Expected sub-section headers here',
  },
  labs: {
    // Page chrome
    breadcrumbLabs: 'GC LABS',
    title: 'GC Labs',
    subtitle:
      'QA bench — simulate GC questions, inspect retrieval, check what the model actually used.',
    // Query panel
    queryPanelHeader: 'QUERY',
    personaLabel: 'PERSONA',
    personaDefault: 'No persona',
    noPersonasYet: 'No personas yet',
    domainLabel: 'DOMAIN',
    domainAll: 'All domains',
    languageLabel: 'LANGUAGE',
    languageAll: 'All',
    languageEn: 'EN',
    languageFr: 'FR',
    queryTextareaLabel: 'TEST QUERY',
    queryTextareaHint: 'Enter to run · Shift+Enter for newline',
    enrichCheckbox: 'Enrich retrieval query',
    retrievalQueryLabel: 'RETRIEVAL QUERY · WHAT GETS EMBEDDED',
    retrievalQueryEmpty: '(empty)',
    retrievalQueryNotPreviewed:
      'Not previewed — the raw query above will be embedded unless you preview.',
    retrievalQueryEnrichedHint:
      'Haiku-transformed on demand — enrichment also runs at Run time',
    retrievalQueryRawHint: 'Raw query — enrichment off',
    previewEnrichmentButton: 'Preview enrichment',
    rePreviewEnrichmentButton: 'Re-preview enrichment',
    previewingButton: 'Previewing…',
    decomposeCheckbox: 'Decompose compound queries',
    decomposeHint:
      'Split a compound question (e.g. "workload AND efficiency AND performance") into 2-3 focused sub-queries. Each is embedded + searched separately; results are unioned. Fixes zero-hit failures where a single blended embedding falls below the similarity floor.',
    subQueriesHeading: 'Sub-queries used',
    compareCheckbox: 'Compare with / without corpus',
    draftsCheckbox: 'Include drafts + in-review entries',
    minScoreLabel: 'Min similarity score',
    minScoreHint:
      "Only chunks scoring at or above this are fed to the coach. Result can be smaller than the top-K cap — that's the point. Typical range: 0.60–0.80. Set to 0 to keep every top-K chunk.",
    runButton: 'Run query',
    runningButton: 'Running…',
    runningElapsedTemplate: 'Running query… {n}s elapsed',
    // Query progress (cycled stage labels while a run is in flight)
    progressStageEnriching: 'Enriching query',
    progressStageRetrieving: 'Retrieving context',
    progressStageGenerating: 'Generating response',
    progressElapsedTemplate: '{s}s elapsed',
    // Response panel
    responseHeader: 'RESPONSE',
    responseReadyTitle: 'Ready when you are',
    responseReadyBody: 'Type a query on the left and hit Run.',
    responseSonnetAloneHeader: 'SONNET ALONE',
    responseWithCorpusHeader: 'WITH CORPUS',
    responseErrorTitle: '⚠ RUN FAILED',
    responseErrorBody:
      'Bedrock returned a throttling error after 2 retries. The retrieval step completed — only generation failed.',
    responseErrorRetry: 'Retry',
    orphanCalloutLabel: '⚠ CITATION NOT IN RETRIEVAL SET',
    orphanCalloutBodyTemplate:
      'The response cites [{n}], but only {m} chunks were retrieved. The model is citing something it was not given — treat that claim as unsupported.',
    zeroRetrievalCalloutLabel: '⚠ ANSWERED WITHOUT CORPUS',
    zeroRetrievalCalloutBody:
      "Nothing was retrieved, so this response is ungrounded — it reflects the model's training data, not your knowledge base. Any citation appearing here would be fabricated by definition.",
    // Coaching-scope decline (guardrail)
    scopeDeclineResponseCalloutLabel: '· COACHING-SCOPE DECLINE',
    scopeDeclineResponseCalloutBody:
      "The coach declined this question per the coaching contract (legal advice, legal research, dispute merits, or another out-of-scope topic). This is expected guardrail behaviour, not a failure. The response above explains what the coach can help with instead.",
    retrievalHeader: 'RETRIEVAL',
    scopeDeclineRetrievalTag: 'NOT USED',
    scopeDeclineRetrievalTitle: 'Retrieval not used for this answer',
    scopeDeclineRetrievalBody:
      "The coach declined per the coaching contract, so retrieval — while it ran on the query — was not the mechanism used to answer. Showing the retrieved chunks would misrepresent what the coach actually did.",
    scopeDeclineRetrievalCountLabel: 'What would have been retrieved',
    scopeDeclineHistoryTag: 'DECLINED',
    scopeDeclineHistoryTagTitle:
      'The coach declined this question per the coaching contract — retrieval fired but was not used to answer.',
    metadataRetrievedTemplate: '{n} retrieved',
    metadataCitedTemplate: '{m}/{n} cited',
    metadataNoContext: 'no context',
    metadataLatencyTemplate: '{s} s',
    citationChipTitle: 'cited',
    citationChipOrphanTitle: 'cited — but not in retrieval set',
    // Compare mode collapse
    compareRetrievalCollapsedTemplate: 'RETRIEVED · {n} — EXPAND ›',
    // Retrieval panel
    retrievedHeaderTemplate: 'RETRIEVED · {n} · SORTED BY SCORE',
    citedChipTemplate: 'cited {m}/{n}',
    citedIndicatorTitle: 'cited',
    notCitedIndicatorTitle: 'not cited',
    expandCitedAsTemplate: 'cited as [{n}]',
    expandNotCited: 'not cited',
    expandChunkIdLabel: 'CHUNK ID',
    openInKb: 'Open in KB ↗',
    retrievalEmpty: 'Retrieval will appear here after you run a query.',
    retrievalZeroTitle: 'No content matched',
    retrievalZeroBody:
      'Try broader terms, remove the domain or language filter, or check that the relevant entries are published.',
    retrievalZeroClearFilters: 'Clear filters',
    retrievalZeroIncludeDrafts: 'Include drafts',
    retrievalErrorNote:
      'Retrieval succeeded — generation failed. These chunks are still valid.',
    // Type badges
    badgeQaPair: 'Q&A',
    badgeFramework: 'FRAMEWORK',
    badgeCaseStudy: 'CASE',
    badgeDocumentExcerpt: 'EXCERPT',
    // Recent tests
    historyHeader: 'RECENT TESTS',
    historyHeaderSubTemplate: 'last {n} · click a row to load its inputs',
    colQuery: 'QUERY',
    colPersona: 'PERSONA',
    colDomain: 'DOMAIN',
    colRetrieved: 'RETRIEVED',
    colCited: 'CITED',
    colBy: 'BY',
    colWhen: 'WHEN',
    loadButton: 'Load',
    deleteRowLabel: 'Delete',
    historyEmpty: 'No tests yet. Run your first query above to build history.',
    deleteConfirmTitle: 'Delete this test run?',
    deleteConfirmBody: 'It disappears from shared history for everyone.',
    deleteConfirmAction: 'Delete',
    deleteConfirmCancel: 'Cancel',
    // Errors
    errorGeneric: "The bench couldn't complete the run. Try again in a moment.",
  },
  kb: {
    breadcrumbKnowledge: 'KNOWLEDGE',
    breadcrumbBase: 'BASE',
    title: 'Knowledge base',
    subTemplate: '{total} entries · {in_review} in review · {draft} drafts.',
    newEntryButton: '+ New entry',
    untitled: 'Untitled entry',
    // Stat cards
    statEntries: 'ENTRIES',
    statPublished: 'PUBLISHED',
    statInReview: 'IN REVIEW',
    statDraft: 'DRAFT',
    // Filter chips
    chipAllTemplate: 'All ({n})',
    chipPublishedTemplate: 'Published ({n})',
    chipInReviewTemplate: 'In review ({n})',
    chipDraftTemplate: 'Draft ({n})',
    chipArchivedTemplate: 'Archived ({n})',
    chipRemovedTemplate: 'Removed ({n})',
    // Dropdown filters
    typeFilterLabel: 'TYPE',
    typeFilterAll: 'All',
    domainFilterLabel: 'DOMAIN',
    domainFilterAll: 'All',
    langFilterLabel: 'LANG',
    langFilterAll: 'All',
    searchPlaceholder: 'Search KB…',
    // Columns
    colTitle: 'TITLE / QUESTION',
    colType: 'TYPE',
    colDomain: 'DOMAIN',
    colContributor: 'CONTRIBUTOR',
    colVersion: 'VERSION',
    colUpdated: 'UPDATED',
    colStatus: 'STATUS',
    colActions: 'ACTIONS',
    // Row-level kebab (V1: Duplicate on case_study only, per Olivier
    // 2026-10-22). Opens a tiny menu with the Duplicate action.
    rowKebabAria: 'Row actions',
    rowKebabDuplicate: '⎘ Duplicate',
    rowKebabDuplicating: 'Duplicating…',
    // Status pills (extends Tenants)
    statusDraft: '● DRAFT',
    statusInReview: '● IN REVIEW',
    statusPublished: '● PUBLISHED',
    statusArchived: '● ARCHIVED',
    statusRemoved: '○ REMOVED',
    // Type labels
    typeQaPair: 'Q&A pair',
    typeFramework: 'Framework',
    typeCaseStudy: 'Case study',
    typeExcerpt: 'Document excerpt',
    // Domain labels (full, used in dropdowns)
    domain: {
      D1_strategy_org: 'D1 — Strategy & Org',
      D2_external_counsel: 'D2 — External Counsel',
      D3_legal_ops_tech: 'D3 — Ops & Tech',
      D4_team_dev: 'D4 — Team & Talent',
      D5_kpis_performance: 'D5 — KPIs & Performance',
      D6_budget_finance: 'D6 — Budget & Finance',
      D7_stakeholder_comms: 'D7 — Stakeholder & Comms',
      D8_governance_risk: 'D8 — Governance & Risk',
    },
    // Empty states
    footerCountTemplate: '{n} ENTRIES',
    emptyTitle: 'No KB entries match',
    emptyBody: 'Try clearing filters or create a new entry.',
    emptyMarketingTitle: 'No KB entries yet',
    emptyMarketingBody: 'Start authoring the editorial corpus.',
    // Editor — chrome
    editorBreadcrumbNew: 'NEW',
    editorBreadcrumbEdit: 'EDIT',
    editorBackLink: '← Back to KB',
    editorBackToList: '← Back to KB',
    editorTitlePlaceholder: 'Untitled entry',
    editorTypeLabel: 'ENTRY TYPE',
    editorExcerptTooltip:
      'Document excerpts come from ingested books — reviewed in the queue, not authored here.',
    editorMetaDomain: 'DOMAIN',
    editorMetaLanguage: 'LANGUAGE',
    editorMetaContributor: 'CONTRIBUTOR',
    editorMetaVersion: 'VERSION',
    editorVersionHistory: 'Version history',
    editorVersionHistoryV1Note:
      'V1.0: current version only. The full version history with citation counts lands in V1.x.',
    // Editor — footer buttons
    editorCancel: 'Cancel',
    editorSaveDraft: 'Save as draft',
    editorSubmitForReview: 'Submit for review',
    editorPublish: 'Publish',
    editorArchive: 'Archive',
    editorUnarchive: 'Unarchive',
    editorRemove: 'Remove',
    editorDuplicate: '⎘ Duplicate',
    editorDuplicateHint:
      'Clone this case study as a new draft. The copy keeps body + metadata; edit only the fields that differ (industry, team size, context).',
    editorRestore: 'Restore entry',
    editorRestoreSuperAdminOnly: 'Only Super Admins can restore a removed entry.',
    editorRestoreForbidden:
      'Restore failed — your account doesn’t have super_admin permission.',
    // Editor — autosave
    autosaveTemplate: 'Saved {when}',
    autosaveNever: 'Not saved yet',
    // qa_pair subform
    fieldQuestion: 'QUESTION',
    fieldQuestionPlaceholder: 'How do I structure my outside-counsel panel?',
    fieldQuestionHint: 'What does the user ask? Use the canonical phrasing.',
    fieldQuestionVariants: 'QUESTION VARIANTS',
    fieldQuestionVariantsHint: 'Alternate phrasings the retrieval matches against.',
    fieldAddVariant: '+ Add variant',
    aiVariantsTrigger: 'Suggest with AI',
    aiVariantsDisabledHelper:
      'Fill in the answer first — suggestions are grounded on it.',
    aiVariantsPanelTitle: 'AI-suggested variants',
    aiVariantsAcceptAll: 'Accept all',
    aiVariantsRejectAll: 'Reject all',
    aiVariantsDismiss: 'Dismiss',
    aiVariantsGroundingToggle: 'Grounded on this answer',
    aiVariantsRowAccept: 'Accept',
    aiVariantsRowEdit: 'Edit',
    aiVariantsRowConfirm: 'Confirm',
    aiVariantsRowReject: 'Reject',
    aiVariantsProvenanceTag: 'AI',
    aiVariantsSuggestMore: 'Suggest more',
    aiVariantsSuggestMoreHelper:
      "New suggestions won't repeat variants you've kept.",
    aiVariantsLoading: 'Generating variants…',
    aiVariantsLoadingSub:
      'Claude is drafting a few phrasings — about 5 seconds.',
    aiVariantsEmpty:
      'No new phrasings this time. Try rephrasing the question, or add variants manually.',
    aiVariantsError:
      "Couldn't reach the suggestion service. You can keep adding variants manually.",
    aiVariantsErrorRetry: 'Try again',
    fieldAnswer: 'ANSWER',
    fieldAnswerPlaceholder: 'The curated response…',
    fieldAnswerHint: 'The curated response. Rich-text editor lands in V1.x; markdown OK in V1.0.',
    // framework subform
    fieldMethodologyName: 'METHODOLOGY NAME',
    fieldWhenToApply: 'WHEN TO APPLY',
    fieldWhenToApplyPlaceholder:
      'e.g. The GC has been asked to rationalize a panel of 30+ external firms in under 6 months.',
    fieldWhenToApplyHint: 'The trigger condition. What situation calls for this methodology?',
    fieldPhases: 'PHASES',
    fieldPhasesHint:
      'Sequential steps of the methodology. Add each phase with its activities and deliverables.',
    fieldAddPhase: '+ Add phase',
    fieldPhaseNumberPrefix: 'PHASE',
    fieldRemovePhase: 'Remove phase',
    fieldPhaseNamePlaceholder: 'Phase name — e.g. "Discovery & current state"',
    fieldPhaseDescriptionPlaceholder: 'What happens in this phase…',
    fieldKeyActivities: 'KEY ACTIVITIES',
    fieldAddActivity: '+ Add activity',
    fieldDeliverables: 'DELIVERABLES',
    fieldAddDeliverable: '+ Add deliverable',
    // case_study subform
    fieldArchetype: 'ARCHETYPE',
    fieldAnonymizedContext: 'ANONYMIZED CONTEXT',
    fieldAnonymizedContextHint:
      'Just enough detail for the retrieval engine to match similar GC profiles — no client-identifying info.',
    fieldSector: 'SECTOR',
    fieldSectorPlaceholder: 'e.g. Tech / SaaS',
    fieldRevenueBand: 'REVENUE BAND',
    fieldRevenueBandPlaceholder: 'e.g. €100M–€500M',
    fieldDeptSize: 'DEPT SIZE',
    fieldDeptSizePlaceholder: 'e.g. 5–15 lawyers',
    fieldGeography: 'GEOGRAPHY',
    fieldGeographyPlaceholder: 'e.g. EU, or FR / DE / BE',
    fieldSituation: 'SITUATION',
    fieldSituationPlaceholder: 'What was the starting context?',
    fieldDecision: 'DECISION',
    fieldDecisionPlaceholder: 'What choice did the GC make, and why?',
    fieldOutcome: 'OUTCOME',
    fieldOutcomePlaceholder: 'What happened next? Hard metrics if available.',
    fieldLessons: 'LESSONS',
    fieldAddLesson: '+ Add lesson',
    // Excerpt review view (when an excerpt entry is opened from the future review queue)
    excerptSourceLabel: 'SOURCE',
    excerptTextLabel: 'EXCERPT',
    excerptSuggestedDomainLabel: 'AUTO-CLASSIFIED DOMAIN',
    excerptReviewLabel: 'EDITORIAL REVIEW',
    excerptApprove: 'Approve',
    excerptRetag: 'Re-tag domain',
    excerptReject: 'Reject',
    excerptRetagDomainLabel: 'NEW DOMAIN',
    excerptRetagConfirm: 'Apply new domain',
    excerptRejectReasonLabel: 'REJECTION REASON (optional)',
    excerptRejectReasonPlaceholder: 'e.g. off-topic; recommends specific legal advice; outdated',
    excerptRejectConfirm: 'Reject excerpt',
    excerptApprovedNote: '✓ Approved — published to retrieval.',
    excerptRejectedNote: '✕ Rejected — archived; excluded from retrieval.',
    // Confirm panels
    confirmYes: 'Confirm',
    confirmCancel: 'Cancel',
    confirmArchiveTitle: 'Archive this entry?',
    confirmArchiveBody:
      'It will be hidden from coaching responses but kept for audit. You can republish it later.',
    confirmRemoveTitle: 'Remove this entry?',
    confirmRemoveBody:
      'Removal is permanent. The entry stays in version history but is hidden from default views and excluded from coaching responses. Only a Super Admin can restore it.',
    confirmRemoveReasonLabel: 'Reason (optional)',
    confirmRemoveReasonPlaceholder: 'e.g. superseded by entry #4012',
    confirmRestoreTitle: 'Restore removed entry?',
    confirmRestoreBody:
      'This brings the entry back as a draft. The original removal stays in the audit log.',
    confirmRestoreTypeLabel: 'Type RESTORE (in capitals) to confirm:',
    confirmRestoreLabel: 'Restore entry',
    // Editor errors
    editorGenericError: 'Something went wrong. Try again.',
    editorRequiredToPublish: 'Fill the following before publishing: {fields}.',
    editorRequiredToSubmit: 'Fill the following before submitting for review: {fields}.',
  },
  // ─── RAG pipeline — search test panel ────────────────────────────────
  kbSearch: {
    breadcrumbInsights: 'INSIGHTS',
    breadcrumbRag: 'RAG PIPELINE',
    title: 'RAG pipeline — Search test',
    subTitle: 'Validate that authored entries surface for the right queries. Composing recommendations + plans from retrieval lands in V1.x.',
    queryLabel: 'QUERY',
    queryPlaceholder: 'e.g. how do I structure my outside counsel panel?',
    typeLabel: 'TYPE',
    typeAll: 'All types',
    domainLabel: 'DOMAIN',
    domainAll: 'All domains',
    langLabel: 'LANG',
    langAll: 'Both',
    topKLabel: 'TOP K',
    includeUnpublishedLabel: 'Include drafts / in-review / archived',
    submitButton: 'Search →',
    hint: 'Embeds the query via Bedrock Titan v2 + ranks by cosine similarity.',
    idleTitle: 'Type a query above',
    idleBody: 'Each result links back to the entry. Click a row to open it in a new tab.',
    errorTitle: 'Search failed',
    errorUpstream: 'Bedrock is temporarily unavailable. Try again in a moment.',
    errorGeneric: 'Something went wrong. Try again.',
    noResultsTitle: 'No matches',
    noResultsBody: 'Nothing in the knowledge base matched “{query}”. Try broadening the filters, including unpublished entries, or rephrasing.',
    resultsCount: '{n} RESULTS',
    openEntryTooltip: 'Open this entry in a new tab',
  },
  // ─── Excerpt review queue (Phase 3) ──────────────────────────────────
  review: {
    pageTitle: 'Excerpt review queue',
    pageSubTemplate: '{count} excerpts pending review · sorted lowest confidence first',
    sortLabel: 'SORT',
    sortConfidenceAsc: 'Confidence ↑ (worst first)',
    sortConfidenceDesc: 'Confidence ↓ (best first)',
    sortUpdated: 'Updated (newest)',
    domainLabel: 'DOMAIN',
    domainAll: 'All domains',
    refreshButton: 'Refresh',
    loading: 'Loading excerpts…',
    emptyTitle: 'Inbox zero',
    emptyBody: 'No excerpts to review. Ingest a source document or change the filters.',
    confidenceTemplate: '{pct}% confidence',
    editedBadge: 'Edited',
    approveButton: 'Approve',
    editButton: 'Edit excerpt',
    retagButton: 'Re-tag domain',
    rejectButton: 'Reject',
    cancelButton: 'Cancel',
    saveButton: 'Save',
    editLabel: 'EXCERPT TEXT',
    editHint:
      'Saving creates an "Edited" badge. Citations will read "Adapted from..." to preserve provenance.',
    retagLabel: 'NEW DOMAIN',
    rejectReasonLabel: 'REASON (OPTIONAL)',
  },
  // ─── Source Documents ────────────────────────────────────────────────
  sources: {
    breadcrumbKnowledge: 'KNOWLEDGE',
    breadcrumbSources: 'SOURCES',
    title: 'Source documents',
    subTemplate: '{total} documents · {processing} processing · {ingested} ingested.',
    uploadButton: '+ Upload book',
    // Stat cards
    statTotal: 'DOCUMENTS',
    statUploaded: 'UPLOADED',
    statProcessing: 'PROCESSING',
    statIngested: 'INGESTED',
    // Filter chips
    chipAllTemplate: 'All ({n})',
    chipUploadedTemplate: 'Uploaded ({n})',
    chipProcessingTemplate: 'Processing ({n})',
    chipIngestedTemplate: 'Ingested ({n})',
    chipFailedTemplate: 'Failed ({n})',
    chipRemovedTemplate: 'Removed ({n})',
    // Filters
    langFilterLabel: 'LANG',
    langFilterAll: 'All',
    searchPlaceholder: 'Search title or author…',
    // Columns
    colTitle: 'TITLE',
    colAuthor: 'AUTHOR',
    colLang: 'LANG',
    colStatus: 'STATUS',
    colChunks: 'EXCERPTS',
    colSize: 'SIZE',
    colUploadedBy: 'UPLOADED BY',
    colUploaded: 'UPLOADED',
    footerCountTemplate: '{n} DOCUMENTS',
    // Status pills + messages
    statusUploaded: '● UPLOADED',
    statusProcessing: '● PROCESSING',
    statusIngested: '● INGESTED',
    statusFailed: '● FAILED',
    statusRemoved: '○ REMOVED',
    statusMessageUploaded: 'File is in storage. Trigger ingestion when ready.',
    statusMessageProcessing: 'Ingestion is running. Excerpts will appear when complete.',
    statusMessageIngested: '{n} excerpts created — they are in the KB review queue.',
    statusMessageFailed: 'Ingestion failed. Check the error below and retry.',
    statusMessageRemoved: 'This document has been soft-removed. Restore to re-use.',
    // Empty states
    emptyTitle: 'No documents match',
    emptyBody: 'Try clearing filters or upload a new document.',
    emptyMarketingTitle: 'No source documents yet',
    emptyMarketingBody:
      'Upload Olivier’s books, reports, or research papers. The ingestion pipeline parses them into excerpts you can review in the KB.',
    // Upload page
    uploadBreadcrumb: 'UPLOAD',
    uploadTitle: 'Upload a source document',
    uploadSubTitle:
      'Fill in the metadata, drop the file. After upload, you can trigger ingestion from the detail page.',
    uploadSuccess: 'Uploaded — opening the detail page.',
    uploadCancel: 'Cancel',
    uploadSubmit: 'Upload →',
    uploadErrorNoFile: 'Pick a file first.',
    uploadErrorTooLarge: 'File is too large — limit is 100 MB.',
    uploadErrorBadType:
      'Unsupported file type. Allowed: .docx, .txt. For a .doc file, open it in Word and Save As → Word Document (.docx). PDF support is coming soon.',
    uploadErrorMissingMetadata: 'Title and author are required.',
    uploadErrorCreateFailed:
      'Could not create the document record. Try again.',
    uploadErrorPutFailed:
      'The file upload to storage failed. Try again from the detail page.',
    // Metadata fields
    fieldTitle: 'TITLE',
    fieldTitlePlaceholder: 'L’impact de l’IA sur les juristes',
    fieldAuthor: 'AUTHOR',
    fieldAuthorPlaceholder: 'Olivier Chaduteau',
    fieldPrefaceBy: 'PREFACE BY',
    fieldPrefaceByPlaceholder: 'Kami Haeri',
    fieldYear: 'PUBLICATION YEAR',
    fieldYearPlaceholder: '2024',
    fieldIsbn: 'ISBN',
    fieldIsbnPlaceholder: '978-1-2345-6789-0',
    fieldLanguage: 'LANGUAGE',
    fieldFilename: 'FILENAME',
    fieldFileSize: 'FILE SIZE',
    fieldChunkCount: 'EXCERPTS CREATED',
    fieldUploadedBy: 'UPLOADED BY',
    fieldIngestedAt: 'INGESTED AT',
    fieldIngestionError: 'INGESTION ERROR',
    // Dropzone
    dropzoneTitle: 'Drop the file here',
    dropzoneBody: 'Or click to browse.',
    dropzoneFormats: '.docx · .txt',
    dropzoneReplaceHint: 'Click to replace.',
    // Detail page
    detailSubTemplate: 'by {author}',
    backToList: '← Back to documents',
    backToDetail: '← Back to document',
    metaSectionTitle: 'Metadata',
    editMetadata: 'Edit metadata',
    editCancel: 'Cancel',
    editSave: 'Save',
    triggerIngestionButton: 'Trigger ingestion →',
    retryIngestionButton: 'Retry ingestion →',
    removeButton: 'Remove',
    restoreButton: 'Restore',
    restoreSuperAdminOnly: 'Only Super Admins can restore a removed document.',
    restoreForbidden:
      'Restore failed — your account doesn’t have super_admin permission.',
    // Confirm panels
    confirmYes: 'Confirm',
    confirmCancel: 'Cancel',
    confirmRemoveTitle: 'Remove this source document?',
    confirmRemoveBody:
      'The document and its excerpts stay in the system but are hidden from default views. Only Super Admins can restore.',
    confirmRemoveReasonLabel: 'Reason (optional)',
    confirmRemoveReasonPlaceholder: 'e.g. duplicate of #1234',
    confirmRestoreTitle: 'Restore this source document?',
    confirmRestoreBody:
      'This brings the document back to the uploaded state. To re-ingest, trigger ingestion afterwards.',
    // Generic errors
    genericError: 'Something went wrong. Try again.',
    notFound: 'Source document not found.',
    // Shared cancel label used by the map-page reject + approve confirm
    // modals (SourceDocumentMapPage). Keep here rather than under
    // `mapping.*` so any other sources-scoped modal can reuse it.
    cancel: 'Cancel',
  },
  mapping: {
    cardTitle: 'Document structure',
    noStructureBody:
      "Before ingesting, propose a chapter + sub-section map. Claude Sonnet 4.6 reads the document and suggests one for the editorial team to review.",
    proposeButton: 'Propose structure',
    // ─── Chunk-size quality badge tooltips ────────────────────────
    // These fire on hover of the size badge on chapters + sub-sections.
    // "Chunk" variants target elements that will BECOME retrieval chunks
    // (every sub-section, plus chapters with zero sub-sections). "Chapter"
    // variants target chapters WITH sub-sections — the chapter itself
    // isn't a chunk, its children are; the badge is informational only.
    // Nested objects (not dotted keys) so the tree-walking t() resolver
    // reaches them via `mapping.chunkSizeTooltipChunk.<tier>`.
    chunkSizeTooltipChunk: {
      tiny: 'Too small (<200 tokens). This chunk may lack standalone context. Consider merging with an adjacent section, or expanding the header pattern to cover a bigger span.',
      good: 'Good size. This chunk will embed cleanly and support high-signal retrieval.',
      large:
        'Large (1,500–3,000 tokens). Retrieval may start returning multiple concepts at once. Consider splitting into 2 sub-sections for cleaner answers.',
      oversized:
        'Oversized (>3,000 tokens). The chunker will auto-split this on paragraph boundaries at ingest, which may lose coherence. Splitting into tighter sub-sections manually gives better results.',
    },
    chunkSizeTooltipChapter: {
      tiny: 'This chapter is very short (<200 tokens total across all sub-sections).',
      good: 'Chapter total size looks healthy — most sub-sections should embed cleanly.',
      large:
        'Chapter is large (1,500–3,000 tokens total). Check sub-section badges — one may be a large chunk.',
      oversized:
        'Chapter is very large (>3,000 tokens total). If sub-sections are also flagged, tighten them; otherwise the chunker will split individual sub-sections at ingest.',
    },
    retryButton: 'Retry propose',
    reviewButton: 'Review & approve',
    reproposeButton: 'Re-propose',
    viewButton: 'View structure',
    proposingTitle: 'Analyzing document…',
    proposingSub:
      'Reading the full text and detecting chapters + sub-sections. Typically ~60 seconds.',
    loading: 'Loading…',
    proposeFailedTitle: 'Structure proposal failed',
    summaryTemplate: '{chapters} chapters · {subSections} sub-sections',
    proposedByModel: 'Proposed by {model}',
    statusProposing: 'PROPOSING',
    statusError: 'ERROR',
    statusPendingReview: 'PENDING REVIEW',
    statusApproved: 'APPROVED',
    statusRejected: 'REJECTED',
    // Map page
    breadcrumbMap: 'Structure',
    pageTitleTemplate: 'Structure · {title}',
    pageSub: 'Review the proposed chapter + sub-section map. Edit, then approve to ingest.',
    emptyChapters: 'No chapters yet. Add one to get started.',
    addChapter: '+ Add chapter',
    addSubSection: 'Add sub-section',
    deleteChapter: 'Remove chapter',
    deleteSubSection: 'Remove sub-section',
    chapterNumberPrefix: 'Chapter',
    chapterTitleLabel: 'TITLE',
    chapterTitlePlaceholder: 'e.g. Bienvenue dans le monde de demain',
    headerPatternLabel: 'HEADER PATTERN',
    headerPatternPlaceholder: 'First few words of the heading as it appears in the document',
    headerPatternHint:
      "The chunker uses this as a regex anchor. Preserve case, accents, and punctuation exactly.",
    subSectionPrefix: 'Sub-section',
    subSectionLabelPlaceholder: 'e.g. 1.A · La rupture digitale',
    saveButton: 'Save changes',
    approveAndIngestButton: 'Approve & ingest',
    rejectButton: 'Reject',
    rejectConfirmTitle: 'Reject this structure?',
    rejectConfirmBody:
      'The structure stays in the system as rejected (for audit). To try again, re-propose from the document detail page.',
    rejectReasonLabel: 'REASON (OPTIONAL)',
    rejectConfirmAction: 'Reject',
    approveConfirmTitle: 'Approve and start ingestion?',
    approveConfirmBody:
      'The map is locked once approved. Ingestion runs against this exact structure. ~210 excerpts will land in the editorial review queue.',
    approveConfirmAction: 'Approve & ingest',
  },
  settings: {
    title: 'Settings',
    lead: 'Platform-wide product settings. Changes apply to every tenant and are recorded in the audit log.',
    group: {
      coach: 'COACH REPLIES',
      content: 'CONTENT OPERATIONS',
      experimental: 'EXPERIMENTAL',
    },
    tag: {
      experimental: 'EXPERIMENTAL',
    },
    state: {
      on: 'ON',
      off: 'OFF',
    },
    audit: 'Last changed by {name} · {when}',
    neverChanged: 'Default — not yet changed',
    history: 'History',
    hideHistory: 'Hide history',
    historyLoading: 'Loading history…',
    historyEmpty: 'No changes yet.',
    historyEntry: '{name} turned it {state}',
    cancel: 'Cancel',
    sources: {
      label: 'Show sources in replies',
      desc: 'Shows tenants which knowledge-base entries each reply drew on: the sources line, the consulted panel and the numbered markers. When off, all three are hidden and the coach stops citing inline.',
      learning: 'We may move this to a per-user preference.',
      effect: 'APPLIES TO NEW REPLIES IMMEDIATELY · ALL TENANTS · EXISTING REPLIES UNCHANGED',
      confirmOn: {
        title: 'Show sources for all tenants?',
        body: 'New replies will show which knowledge-base entries they drew on.',
      },
      confirmOff: {
        title: 'Hide sources for all tenants?',
        body: 'New replies will no longer show the sources line, the consulted panel or inline markers. Replies already given are unchanged.',
      },
      hide: 'Hide sources',
      show: 'Show sources',
    },
    error: {
      load: 'Could not load settings. Try again.',
      save: 'Could not save setting. Try again.',
    },
  },
} satisfies StringTree;

const fr: typeof en = {
  common: {
    or: 'OU',
    back: 'Retour',
    signOut: 'Se déconnecter',
  },
  brand: {
    // Admin-flavor chrome (FR). See EN block for spec reference.
    wordmark: 'GC COACH',
    wordmarkBadge: 'ADMIN',
    wordmarkSub: 'BACK OFFICE INTERNE',
    eyebrow: 'OUTIL INTERNE · ACCÈS RESTREINT',
    headline: 'Console d’administration.',
    tagline:
      'Gestion du contenu, des tenants et de la base de connaissances. Réservé à l’équipe interne GC Coach.',
    footerLeft: 'ENV · STAGING',
    footerRight: 'EU · FR / EN',
    utilityBarLeft: 'GC COACH · CONSOLE ADMIN',
    utilityBarRight: 'ACCÈS INTERNE · admin.gc-coach.ai',
  },
  login: {
    eyebrow: 'CONNEXION',
    headline: 'Bon retour',
    emailLabel: 'EMAIL PROFESSIONNEL',
    emailPlaceholder: 'florence.larrieu@acme.com',
    passwordLabel: 'MOT DE PASSE',
    rememberDevice: 'Se souvenir de cet appareil',
    forgotPassword: 'Mot de passe oublié ?',
    submitButton: 'Se connecter →',
    ssoMicrosoft: 'Continuer avec Microsoft',
    ssoGoogle: 'Continuer avec Google',
    ssoEnterprise: 'Authentification unique',
    footerLine: 'Pas encore de compte ? L’accès à GC Coach se fait via votre direction juridique.',
    footerLink: 'Demander l’accès',
    errorInvalidCredentials: 'Cet email ou mot de passe n’a pas fonctionné. Réessayez.',
    errorRateLimited: 'Trop de tentatives. Patientez un instant, puis réessayez.',
  },
  firstLogin: {
    eyebrow: 'BIENVENUE · PREMIÈRE CONNEXION',
    headline: 'Définissez votre mot de passe',
    body:
      'Votre compte {email} a été créé par votre administrateur. Choisissez un mot de passe pour terminer la configuration — vous activerez ensuite la vérification en deux étapes.',
    newPasswordLabel: 'NOUVEAU MOT DE PASSE',
    confirmPasswordLabel: 'CONFIRMER',
    submitButton: 'Définir le mot de passe →',
    callout:
      'Prochaine étape : configurer votre application d’authentification. Gardez votre téléphone à portée.',
    errorMismatch: 'Les mots de passe ne correspondent pas.',
    errorWeakPassword: 'Ce mot de passe ne respecte pas les critères requis.',
  },
  mfaChallenge: {
    eyebrow: 'VÉRIFICATION EN DEUX ÉTAPES',
    headline: 'Confirmons votre identité',
    bodyTemplate:
      'Entrez le code à 6 chiffres généré par votre application d’authentification pour {email}.',
    codeLabel: 'CODE D’AUTHENTIFICATION',
    trustDeviceTemplate: 'Faire confiance à cet appareil pour {days} jours',
    trustDevicePolicyTemplate: 'POLITIQUE ACME · {days} J MAX',
    submitButton: 'Vérifier →',
    fallbackEmail: 'Utiliser un code par email à la place',
    fallbackRecovery: 'Utiliser un code de récupération',
    fallbackLostDevice: 'J’ai perdu mon authentificateur',
    fallbackHelp: 'Je n’ai accès à aucune méthode',
    errorWrongCode: 'Ce code n’a pas fonctionné. Réessayez.',
  },
  mfaRecoveryCode: {
    eyebrow: 'RÉCUPÉRATION DE COMPTE',
    headline: 'Vérifiez votre email',
    bodyTemplate:
      'Nous avons envoyé un code à 6 chiffres à l’adresse associée à {email}. Entrez-le ci-dessous pour configurer un nouvel authentificateur.',
    bodyGeneric:
      'Nous avons envoyé un code à 6 chiffres à l’adresse associée à votre compte. Entrez-le ci-dessous pour configurer un nouvel authentificateur.',
    codeLabel: 'CODE DE RÉCUPÉRATION',
    submitButton: 'Vérifier →',
    expiresCaption: 'LE CODE EXPIRE DANS 10 MINUTES',
    cancelLink: '← Annuler et me connecter autrement',
    errorWrongCode: 'Ce code n’a pas fonctionné. Réessayez.',
    errorExpired: 'Ce code a expiré. Recommencez la récupération.',
  },
  mfaSetup: {
    eyebrow: 'ACTIVER LA VÉRIFICATION EN DEUX ÉTAPES',
    eyebrowRecovery: 'CONFIGURER UN NOUVEL AUTHENTIFICATEUR',
    headlineScan: 'Scannez le QR code',
    bodyScan:
      'Ouvrez votre application d’authentification (Google Authenticator, 1Password, Authy…) et scannez ce code.',
    manualKeyLabel: 'OU ENTREZ CETTE CLÉ MANUELLEMENT',
    accountCaptionTemplate: 'COMPTE · GC COACH ({email})',
    submitScan: 'J’ai scanné — continuer →',
    callout:
      'À l’étape suivante, nous vous donnerons 10 codes de récupération à conserver hors ligne, en cas de perte de votre téléphone.',
    stepperScan: 'Scanner',
    stepperVerify: 'Vérifier',
    stepperSave: 'Sauvegarder',
    headlineVerify: 'Vérifiez le code',
    bodyVerify: 'Entrez le code à 6 chiffres affiché par votre application.',
    submitVerify: 'Vérifier →',
    headlineSave: 'Sauvegardez vos codes de récupération',
    bodySave:
      'Conservez ces codes à usage unique en lieu sûr (gestionnaire de mots de passe). Chacun peut être utilisé une fois si vous perdez l’accès à votre application.',
    download: 'Télécharger les codes',
    copy: 'Copier dans le presse-papiers',
    confirmSaved: 'J’ai sauvegardé ces codes',
    finishButton: 'Terminer la configuration',
  },
  forgotPassword: {
    eyebrow: 'RÉINITIALISATION',
    headline: 'Mot de passe oublié ?',
    body:
      'Entrez l’email associé à votre compte. Si nous le reconnaissons, vous recevrez un lien dans la minute.',
    emailLabel: 'EMAIL PROFESSIONNEL',
    emailPlaceholder: 'florence.larrieu@acme.com',
    submitButton: 'Envoyer le lien →',
    backLink: '← Retour à la connexion',
    successHeadline: 'Vérifiez votre boîte mail',
    successBodyTemplate:
      'Si {email} est enregistré, un lien de réinitialisation est en route. Il est valide pendant 30 minutes.',
    successResend: 'Pas reçu ? Réessayer',
    errorRateLimited: 'Trop de tentatives. Patientez un instant, puis réessayez.',
    errorGeneric: 'Impossible de demander la réinitialisation. Réessayez.',
  },
  passwordReset: {
    eyebrow: 'NOUVEAU MOT DE PASSE',
    headline: 'Choisissez un nouveau mot de passe',
    bodyTemplate: 'Pour {email}. Le lien est valide pendant 30 minutes.',
    codeLabel: 'CODE REÇU PAR EMAIL',
    codeHint: 'Collez le code à 6 chiffres que nous vous avons envoyé.',
    newPasswordLabel: 'NOUVEAU MOT DE PASSE',
    confirmPasswordLabel: 'CONFIRMER',
    submitButton: 'Mettre à jour le mot de passe →',
    errorCodeInvalid: 'Ce code semble incorrect. Vérifiez votre email et réessayez.',
    errorCodeExpired: 'Ce code a expiré. Demandez un nouveau lien de réinitialisation.',
    errorWeakPassword: 'Ce mot de passe ne respecte pas les critères requis.',
    errorMismatch: 'Les mots de passe ne correspondent pas.',
    errorGeneric: 'Impossible de mettre à jour le mot de passe. Réessayez.',
    successHeadline: 'Mot de passe mis à jour',
    successBody: 'Vous pouvez désormais vous connecter avec votre nouveau mot de passe.',
    successButton: 'Continuer vers la connexion →',
  },
  emailVerify: {
    eyebrow: 'VÉRIFICATION D’EMAIL',
    headline: 'Vérifiez votre boîte mail',
    bodyTemplate:
      'Nous avons envoyé un lien de vérification à {email}. Cliquez dessus depuis cet appareil pour activer votre compte.',
    waitingStatus: 'En attente de confirmation…',
    waitingCaption: 'CET ÉCRAN SE MET À JOUR AUTOMATIQUEMENT',
    resendPrefix: 'Pas reçu ?',
    resendCountdownTemplate: 'RENVOYER DANS {seconds} S',
    resendButton: 'Renvoyer',
    changeEmailLink: 'Modifier l’adresse email',
    verifyingStatus: 'Vérification…',
    successHeadline: 'Email vérifié',
    successBody: 'Vous pouvez désormais vous connecter à GC Coach.',
    successButton: 'Continuer vers la connexion →',
    errorHeadline: 'Échec de la vérification',
    errorBody:
      'Ce lien n’est plus valide. Demandez un nouvel email de vérification ou contactez le support.',
    errorRequestNew: 'Demander un nouveau lien',
  },
  sessionExpired: {
    badge: 'SESSION EXPIRÉE',
    captionTemplate: 'INACTIF DEPUIS {minutes} MIN',
    headline: 'Reconnectez-vous pour continuer',
    body:
      'Pour votre sécurité, nous demandons une nouvelle authentification après une période d’inactivité.',
    accountLabel: 'COMPTE',
    passwordLabel: 'MOT DE PASSE',
    submitButton: 'Reprendre la session →',
    signOutLink: 'Se déconnecter à la place',
    saveCaption: 'TOUT TRAVAIL EN COURS EST SAUVEGARDÉ',
    errorWrongPassword: 'Ce mot de passe n’a pas fonctionné. Réessayez.',
    errorRateLimited: 'Trop de tentatives. Patientez un instant, puis réessayez.',
    errorGeneric: 'Impossible de se reconnecter. Réessayez.',
  },
  shell: {
    brandWordmark: 'GC Coach',
    brandSub: 'ADMIN · BACK OFFICE',
    sectionOverview: 'APERÇU',
    sectionKnowledge: 'CONNAISSANCES',
    sectionInsights: 'ANALYSES',
    sectionCustomers: 'CLIENTS',
    sectionAdministration: 'ADMINISTRATION',
    navDashboard: 'Tableau de bord',
    navAnalytics: 'Analytique',
    navNotifications: 'Statut système',
    navSources: 'Documents sources',
    navReview: 'File de validation',
    navKb: 'Base de connaissances',
    navFrameworks: 'Frameworks',
    navPersonae: 'Personas',
    navBenchmark: 'Benchmark',
    navRag: 'Pipeline RAG',
    navLabs: 'GC Labs',
    navTenants: 'Tenants',
    navModeration: 'Modération',
    navBilling: 'Facturation',
    navUsers: 'Utilisateurs & rôles',
    navAudit: 'Journal d’audit',
    navSettings: 'Paramètres',
    regionLabel: 'EU-WEST-1',
    notificationsAriaLabel: 'Notifications',
    userMenuMyProfile: 'Mon profil',
    userMenuPreferences: 'Préférences',
    userMenuShortcuts: 'Raccourcis clavier',
    userMenuSignOut: 'Se déconnecter',
    stateLoading: 'CHARGEMENT…',
    state401Title: 'Session expirée',
    state401Body: 'Votre session a expiré. Reconnectez-vous pour continuer.',
    state401Button: 'Se reconnecter →',
    stateNetworkTitle: 'Connexion perdue',
    stateNetworkBody: 'Impossible de joindre le serveur. Vérifiez votre connexion — nous réessayons automatiquement.',
    stateNetworkButton: 'Réessayer',
  },
  roles: {
    super_admin: 'Super administrateur',
    content_editor: 'Éditeur de contenu',
    customer_success: 'Customer Success',
    analyst: 'Analyste',
    read_only: 'Lecture seule',
  },
  roleDescriptions: {
    super_admin: 'Accès total · facturation · paramètres · gestion utilisateurs',
    content_editor: 'KB · frameworks · publications',
    customer_success: 'Tenants · onboarding · support',
    analyst: 'Analytique · benchmark · export',
    read_only: 'Lecture seule, audit',
  },
  permissionLabels: {
    'kb:read': 'Voir KB & Q&R',
    'kb:write': 'Modifier KB & Q&R',
    'sources:write': 'Ingérer documents sources',
    'frameworks:write': 'Créer des frameworks',
    'analytics:read': 'Voir analytique',
    'benchmark:read': 'Voir benchmark',
    'benchmark:write': 'Modifier données benchmark',
    'tenants:read': 'Voir les tenants',
    'tenants:write': 'Gérer les tenants',
    'moderation:read': 'File de modération',
    'billing:write': 'Gérer la facturation',
    'users:read': 'Voir l’équipe',
    'users:write': 'Gérer équipe & rôles',
    'settings:read': 'Voir paramètres système',
    'settings:write': 'Paramètres système',
    'audit:read': 'Voir journal d’audit',
  },
  permissionGroups: {
    knowledge: 'CONNAISSANCES',
    insights: 'ANALYSES',
    customers: 'CLIENTS',
    admin: 'ADMINISTRATION',
  },
  users: {
    breadcrumbTeam: 'ÉQUIPE',
    breadcrumbUsers: 'UTILISATEURS & RÔLES',
    breadcrumbRoles: 'RÔLES',
    title: 'Utilisateurs & rôles',
    subTemplate: 'L’équipe interne GC Coach. {active} actifs · {invited} invités · {suspended} suspendus.',
    manageRolesButton: 'Gérer les rôles',
    inviteButton: '+ Inviter un utilisateur',
    statUsers: 'UTILISATEURS',
    statActive: 'ACTIFS',
    statPending: 'INVITATIONS EN ATTENTE',
    statMfa: 'MFA ACTIVÉE',
    statMfaPendingTemplate: '{count} en attente de configuration',
    chipAllTemplate: 'Tous ({n})',
    chipActiveTemplate: 'Actifs ({n})',
    chipInvitedTemplate: 'Invités ({n})',
    chipSuspendedTemplate: 'Suspendus ({n})',
    chipRemovedTemplate: 'Supprimés ({n})',
    roleFilterLabel: 'RÔLE',
    roleFilterAll: 'Tous',
    searchPlaceholder: 'Rechercher…',
    colUser: 'UTILISATEUR',
    colRole: 'RÔLE',
    colRegion: 'RÉGION',
    colMfa: 'MFA',
    colLastSeen: 'DERNIER ACCÈS',
    colStatus: 'STATUT',
    mfaOn: '● ACTIVÉE',
    mfaOff: '○ —',
    statusActive: '● ACTIF',
    statusInvited: '● INVITÉ',
    statusSuspended: '● SUSPENDU',
    statusRemoved: '○ SUPPRIMÉ',
    invitedRowName: 'Invitation envoyée',
    footerCountTemplate: '{n} UTILISATEURS · PAS DE PAGINATION (ÉQUIPE INTERNE)',
    emptyTitle: 'Aucun utilisateur',
    emptyBody: 'Essayez d’effacer les filtres ou d’inviter un collègue.',
    inviteTitle: 'Inviter un utilisateur',
    inviteIntro:
      'L’utilisateur recevra une invitation par email. À la première connexion, il définira son mot de passe puis activera la MFA (obligatoire).',
    fieldEmail: 'EMAIL PROFESSIONNEL',
    fieldEmailPlaceholderTemplate: 'nom@{firstDomain}',
    fieldEmailHintTemplate: 'Domaines autorisés : {domains}',
    fieldFirstName: 'PRÉNOM',
    fieldLastName: 'NOM',
    fieldRole: 'RÔLE',
    fieldRegion: 'RÉGION DE DONNÉES',
    regionEuW1: 'EU-W1 (Dublin)',
    permissionPreview: 'PERMISSIONS ACCORDÉES',
    sendInvite: 'Envoyer l’invitation',
    cancel: 'Annuler',
    inviteErrorEmailDomainTemplate: 'L’email doit utiliser l’un des domaines autorisés : {domains}.',
    inviteErrorEmailFormat: 'Saisissez un email valide.',
    inviteErrorRequired: 'Ce champ est requis.',
    inviteErrorDuplicate: 'Un utilisateur avec cet email existe déjà.',
    inviteErrorGeneric: 'Impossible d’envoyer l’invitation. Réessayez.',
    detailMetaRole: 'RÔLE',
    detailMetaRegion: 'RÉGION',
    detailMetaMfa: 'MFA',
    detailMetaTenure: 'ANCIENNETÉ',
    detailMetaLastSeen: 'DERNIER ACCÈS',
    detailMetaCreatedBy: 'CRÉÉ PAR',
    detailMfaEnabled: 'TOTP · activée',
    detailMfaPending: 'TOTP · en attente',
    detailMfaNone: '—',
    detailChangeRole: 'CHANGER LE RÔLE',
    detailPermissions: 'PERMISSIONS',
    detailActiveSessions: 'SESSIONS ACTIVES',
    detailSessionsBody:
      'Le suivi par session arrive plus tard. Pour l’instant vous pouvez révoquer toutes les sessions d’un clic.',
    detailSignOutAll: 'Déconnecter de tous les appareils',
    detailSendReset: 'Réinitialiser le mot de passe',
    detailSendResetActiveOnly:
      'Réinitialisation du mot de passe disponible uniquement pour les utilisateurs actifs. Restaurez ou réactivez d’abord.',
    detailResetMfa: 'Réinitialiser la MFA',
    detailSuspend: 'Suspendre l’accès',
    detailReactivate: 'Réactiver l’accès',
    detailRemove: 'Supprimer',
    detailRestore: 'Restaurer l’utilisateur',
    detailRestoreSuperAdminOnly:
      'Seuls les Super Administrateurs peuvent restaurer un utilisateur supprimé.',
    detailRestoreForbidden:
      'Restauration impossible — votre compte n’a pas la permission super_admin.',
    detailConfirmRemove: 'Supprimer cet utilisateur ?',
    detailConfirmRemoveBody:
      'La suppression est un signal de départ permanent. Le compte est conservé pour l’audit mais masqué de la vue par défaut. Il ne pourra plus se reconnecter sauf si un Super Administrateur le restaure.',
    detailConfirmRemoveReasonLabel: 'Motif (facultatif)',
    detailConfirmRemoveReasonPlaceholder: 'ex. départ de l’entreprise le 30/06/2026',
    detailConfirmRestore: 'Restaurer l’utilisateur supprimé',
    detailConfirmRestoreBody:
      'Cet utilisateur avait été supprimé. La restauration le ramène à l’état Actif et réactive sa connexion. La suppression initiale reste tracée dans le journal d’audit.',
    detailConfirmRestoreTypeLabel: 'Tapez RESTORE (en majuscules) pour confirmer :',
    detailConfirmSuspend: 'Suspendre cet utilisateur ?',
    detailConfirmSuspendBody:
      'Il sera déconnecté partout et ne pourra plus se reconnecter jusqu’à réactivation.',
    detailConfirmYes: 'Confirmer',
    detailConfirmCancel: 'Annuler',
    detailResendInvite: 'Renvoyer l’invitation',
    detailGenericError: 'Une erreur est survenue. Réessayez.',
    detailTenureNever: 'Jamais connecté',
    detailLastSeenNever: 'Jamais',
    rolesTitle: 'Rôles & permissions',
    rolesSub:
      '5 rôles prédéfinis. Les permissions sont appliquées côté serveur (RBAC) — cette grille est la référence.',
    rolesBackButton: '← Retour aux utilisateurs',
    rolesCallout:
      'Les 5 rôles de base sont en lecture seule (verrouillés). Créez un rôle personnalisé pour ajuster une combinaison de permissions. Phase post-MVP.',
    rolesColUsersTemplate: '{n} UTIL. · VERROUILLÉ',
  },
  tenants: {
    breadcrumbCustomers: 'CLIENTS',
    breadcrumbTenants: 'TENANTS',
    title: 'Tenants',
    subTemplate: '{active} actifs · {suspended} suspendus · {trial} essai.',
    newTenantButton: '+ Nouveau tenant',
    statTenants: 'TENANTS',
    statActive: 'ACTIFS',
    statTrial: 'ESSAI',
    statPastDue: 'IMPAYÉS',
    chipAllTemplate: 'Tous ({n})',
    chipActiveTemplate: 'Actifs ({n})',
    chipSuspendedTemplate: 'Suspendus ({n})',
    chipRemovedTemplate: 'Supprimés ({n})',
    standingFilterLabel: 'STANDING',
    standingFilterAll: 'Tous',
    jurisdictionFilterLabel: 'JURIDICTION',
    jurisdictionFilterAll: 'Toutes',
    searchPlaceholder: 'Rechercher…',
    colTenant: 'TENANT',
    colJurisdiction: 'JURIDICTION',
    colRegion: 'RÉGION',
    colPlan: 'OFFRE',
    colStanding: 'STANDING',
    colUsers: 'UTIL.',
    colCreated: 'CRÉÉ',
    colStatus: 'STATUT',
    statusActive: '● ACTIF',
    statusSuspended: '● SUSPENDU',
    statusRemoved: '○ SUPPRIMÉ',
    standingTrial: 'ESSAI',
    standingActive: 'ACTIF',
    standingPastDue: 'IMPAYÉ',
    standingChurned: 'RÉSILIÉ',
    footerCountTemplate: '{n} TENANTS · PAS DE PAGINATION (V1)',
    emptyTitle: 'Aucun tenant ne correspond',
    emptyBody: 'Essayez de retirer des filtres ou créez un nouveau tenant.',
    emptyMarketingTitle: 'Aucun tenant pour l’instant',
    emptyMarketingBody:
      'Créez la première organisation cliente pour commencer à provisionner GC Coach.',
    // Wizard
    wizardTitleStep1: 'Nouveau tenant — Étape 1 sur 2 · Détails',
    wizardTitleStep2: 'Nouveau tenant — Étape 2 sur 2 · Premier administrateur',
    wizardNext: 'Suivant →',
    wizardBack: '← Retour',
    wizardCreate: 'Créer le tenant',
    wizardSkipAndCreate: 'Ignorer et créer',
    cancel: 'Annuler',
    wizardDeferralTitle: 'Différé en V1',
    wizardDeferralBody:
      'En V1, les tenants sont créés mais les administrateurs ne sont pas encore invités. Renseignez ces informations dès maintenant pour préremplir l’invitation lors du lancement du portail en Phase 1. Sautez cette étape pour créer uniquement le tenant.',
    fieldName: 'NOM DE L’ORGANISATION',
    fieldNamePlaceholder: 'Direction Juridique Acme',
    fieldSlug: 'IDENTIFIANT',
    fieldSlugPlaceholder: 'acme-juridique',
    fieldSlugHint: 'Auto-dérivé du nom ; immuable après création.',
    fieldJurisdiction: 'JURIDICTION',
    fieldRegion: 'RÉGION DE DONNÉES',
    regionEuW1: 'EU-W1 (Dublin)',
    regionUsE1: 'US-E1 (Virginie du Nord)',
    regionApSe1: 'AP-SE1 (Singapour)',
    regionEnterpriseTier: 'Offre Enterprise',
    fieldStanding: 'STANDING',
    standingTrialLabel: 'Essai — évaluation pré-payée',
    standingTrialDesc:
      'Par défaut pour les nouveaux tenants ; convertir en Actif après paiement.',
    standingActiveLabel: 'Actif — client payant',
    standingActiveDesc: 'Le tenant a un abonnement payé.',
    addBillingDetails: 'Ajouter les détails de facturation ▾',
    fieldBillingEmail: 'EMAIL DE FACTURATION',
    fieldPlanTier: 'OFFRE',
    fieldSeatCount: 'NOMBRE DE SIÈGES',
    fieldAdminEmail: 'EMAIL',
    fieldAdminEmailHint: 'Ne doit pas être une adresse @gc-coach.ai',
    fieldAdminFirstName: 'PRÉNOM',
    fieldAdminLastName: 'NOM',
    fieldAdminRole: 'RÔLE',
    adminRoleChipLabel: 'TENANT ADMIN',
    fieldErrorRequired: 'Requis',
    fieldErrorEmailFormat: 'Email invalide',
    fieldErrorInternalDomain:
      'Impossible d’utiliser @gc-coach.ai (domaine du personnel interne)',
    fieldErrorInvalidNumber: 'Doit être un nombre entier positif',
    fieldErrorDuplicateName: 'Un tenant avec ce nom existe déjà',
    createGenericError: 'Création impossible. Réessayez.',
    // Detail drawer
    detailMetaJurisdiction: 'JURIDICTION',
    detailMetaRegion: 'RÉGION',
    detailMetaStanding: 'STANDING',
    detailMetaUsers: 'UTILISATEURS',
    detailMetaCreatedBy: 'CRÉÉ PAR',
    detailMetaCreatedAt: 'CRÉÉ LE',
    detailSlug: 'IDENTIFIANT',
    detailSlugPermanent: 'L’identifiant est permanent.',
    detailEditMetadata: 'MODIFIER LES MÉTADONNÉES',
    detailSaveMetadata: 'Enregistrer',
    detailChangeStanding: 'CHANGER LE STANDING',
    detailTenantUsers: 'UTILISATEURS DU TENANT',
    detailTenantUsersEmpty:
      'Aucun utilisateur du tenant pour l’instant. Les invitations administrateur arriveront en Phase 1 avec le lancement du portail.',
    detailTenantUsersHint:
      'Invitez, suspendez et gérez les utilisateurs de ce tenant sur la page dédiée.',
    detailTenantUsersManage: 'Gérer les utilisateurs →',
    detailRemovalMeta: 'SUPPRESSION',
    detailRemovedAtTemplate: 'Supprimé {when} par {by}',
    detailSuspend: 'Suspendre l’accès',
    detailReactivate: 'Réactiver l’accès',
    detailRemove: 'Supprimer le tenant',
    detailRestore: 'Restaurer le tenant',
    detailRestoreSuperAdminOnly:
      'Seuls les Super Administrateurs peuvent restaurer un tenant supprimé.',
    detailRestoreForbidden:
      'Restauration impossible — votre compte n’a pas la permission super_admin.',
    detailConfirmSuspend: 'Suspendre ce tenant ?',
    detailConfirmSuspendBody:
      'Tous les utilisateurs du tenant seront bloqués jusqu’à la réactivation.',
    detailConfirmRemove: 'Supprimer ce tenant ?',
    detailConfirmRemoveBody:
      'La suppression est un signal d’offboarding permanent. L’enregistrement est conservé pour l’audit mais masqué de la vue par défaut. Les utilisateurs du tenant seront anonymisés selon notre politique de rétention. Seul un Super Administrateur peut le restaurer.',
    detailConfirmRemoveReasonLabel: 'Motif (facultatif)',
    detailConfirmRemoveReasonPlaceholder: 'ex. contrat terminé le 2026-12-31',
    detailConfirmRestore: 'Restaurer le tenant supprimé ?',
    detailConfirmRestoreBody:
      'Ce tenant a été supprimé. La restauration le repasse en Actif et réactive ses opérations. La suppression initiale reste dans le journal d’audit.',
    detailConfirmRestoreTypeLabel:
      'Tapez RESTORE (en majuscules) pour confirmer :',
    detailConfirmYes: 'Confirmer',
    detailConfirmCancel: 'Annuler',
    detailGenericError: 'Une erreur est survenue. Réessayez.',
  },
  tenantUsers: {
    breadcrumbUsers: 'UTILISATEURS',
    titleTemplate: '{name} · Utilisateurs',
    titleFallback: 'Utilisateurs du tenant',
    subTemplate: '{active} actifs · {invited} invités · {suspended} suspendus.',
    backToTenant: '← Retour au tenant',
    inviteButton: '+ Inviter un utilisateur',
    tenantNotFound: 'Tenant introuvable.',
    statUsers: 'UTILISATEURS',
    statActive: 'ACTIFS',
    statInvited: 'INVITÉS',
    statSuspended: 'SUSPENDUS',
    chipGroupLabel: 'Filtre de statut',
    chipAllTemplate: 'Tous ({n})',
    chipActiveTemplate: 'Actifs ({n})',
    chipInvitedTemplate: 'Invités ({n})',
    chipSuspendedTemplate: 'Suspendus ({n})',
    chipRemovedTemplate: 'Supprimés ({n})',
    chipAnonymizedTemplate: 'Anonymisés ({n})',
    searchPlaceholder: 'Rechercher par email ou nom…',
    colUser: 'UTILISATEUR',
    colRole: 'RÔLE',
    colLastSeen: 'DERNIÈRE CONN.',
    colStatus: 'STATUT',
    statusActive: '● ACTIF',
    statusInvited: '● INVITÉ',
    statusSuspended: '● SUSPENDU',
    statusRemoved: '○ SUPPRIMÉ',
    statusAnonymized: '○ ANONYMISÉ',
    roleTenantAdmin: 'Admin tenant',
    roleTenantAdminLocked: 'Admin tenant (rôle V1 — fixe)',
    invitedRowName: '(invitation en attente)',
    emptyTitle: 'Aucun utilisateur du tenant ne correspond',
    emptyBody: 'Effacez les filtres ou invitez le premier utilisateur.',
    footerCountTemplate: '{n} UTILISATEURS · PAS DE PAGINATION (V1)',
    inviteTitle: 'Inviter un utilisateur du tenant',
    inviteIntro:
      'L’utilisateur invité recevra un email avec les instructions de connexion. Il rejoint ce tenant avec le rôle Admin tenant.',
    fieldEmail: 'EMAIL',
    fieldEmailPlaceholder: 'nom@entreprise.com',
    fieldFirstName: 'PRÉNOM',
    fieldLastName: 'NOM',
    fieldRole: 'RÔLE',
    sendInvite: 'Envoyer l’invitation',
    cancel: 'Annuler',
    inviteErrorRequired: 'Champ requis',
    inviteErrorEmailFormat: 'Saisissez un email valide',
    inviteErrorDuplicate: 'Un utilisateur avec cet email existe déjà pour ce tenant.',
    inviteErrorTenantInactive:
      'Ce tenant n’est pas actif. Réactivez-le avant d’inviter des utilisateurs.',
    inviteErrorGeneric: 'L’invitation n’a pas pu être envoyée. Réessayez.',
    detailMetaRole: 'RÔLE',
    detailMetaLocale: 'LOCALE',
    detailMetaMfa: 'MFA',
    detailMetaTenure: 'ANCIENNETÉ',
    detailMetaLastSeen: 'DERNIÈRE CONN.',
    detailMetaCreatedBy: 'INVITÉ PAR',
    detailMfaEnabled: 'Activé',
    detailMfaPending: 'En attente',
    detailMfaNone: 'Aucun',
    detailLastSeenNever: 'Jamais connecté',
    detailResendInvite: 'Renvoyer l’invitation',
    detailSuspend: 'Suspendre l’accès',
    detailReactivate: 'Réactiver l’accès',
    detailRemove: 'Supprimer l’utilisateur',
    detailAnonymize: 'Anonymiser (RGPD)',
    detailAnonymizedNote: 'Cet utilisateur a été anonymisé. Aucune action supplémentaire n’est possible.',
    detailAnonymizeSuperAdminOnly: 'Seuls les Super Administrateurs peuvent anonymiser un utilisateur supprimé.',
    detailAnonymizeForbidden:
      'Anonymisation impossible — votre compte n’a pas la permission super_admin.',
    detailConfirmSuspend: 'Suspendre cet utilisateur ?',
    detailConfirmSuspendBody:
      'L’utilisateur sera bloqué jusqu’à la réactivation. Ses sessions actives seront fermées immédiatement.',
    detailConfirmRemove: 'Supprimer cet utilisateur ?',
    detailConfirmRemoveBody:
      'La suppression est un signal d’offboarding souple. L’utilisateur est masqué de la vue par défaut et bloqué. L’enregistrement est conservé pour l’audit. Un Super Administrateur pourra ensuite l’anonymiser (effacement RGPD).',
    detailConfirmRemoveReasonLabel: 'Motif (facultatif)',
    detailConfirmRemoveReasonPlaceholder: 'ex. départ de l’entreprise le 2026-12-31',
    detailConfirmAnonymize: 'Anonymiser cet utilisateur ? Action irréversible.',
    detailConfirmAnonymizeBody:
      'L’effacement RGPD remplace l’email et le nom par des valeurs sentinelles et désactive le compte Cognito. L’enregistrement subsiste pour l’intégrité des clés étrangères mais ne contient plus aucune donnée personnelle. Action irréversible.',
    detailConfirmAnonymizeTypeLabel: 'Tapez ANONYMIZE (en majuscules) pour confirmer :',
    detailConfirmYes: 'Confirmer',
    detailConfirmCancel: 'Annuler',
    detailGenericError: 'Une erreur est survenue. Réessayez.',
  },
  frameworks: {
    breadcrumbKnowledge: 'CONNAISSANCES',
    breadcrumbFrameworks: 'FRAMEWORKS',
    // ─── Vue 8 · Bibliothèque ───────────────────────────────────────────
    libraryTitle: 'Bibliothèque de frameworks',
    librarySubTemplate:
      '{total} frameworks · {published} publiés · {draft} brouillons · {retired} retirés.',
    newFrameworkButton: '+ Nouveau framework',
    statTotal: 'TOTAL',
    statPublished: 'PUBLIÉS',
    statDraft: 'BROUILLONS',
    statRetired: 'RETIRÉS',
    chipAllTemplate: 'Tous ({n})',
    chipDraftTemplate: 'Brouillons ({n})',
    chipPublishedTemplate: 'Publiés ({n})',
    chipRetiredTemplate: 'Retirés ({n})',
    domainFilterLabel: 'DOMAINE',
    domainFilterAll: 'Tous',
    searchPlaceholder: 'Rechercher…',
    colTitle: 'TITRE',
    colDomain: 'DOMAINE',
    colStatus: 'STATUT',
    colVersion: 'VERSION',
    colActivations: 'ACTIVATIONS',
    colLastEdited: 'MODIFIÉ',
    colEditedBy: 'PAR',
    statusDraft: '● BROUILLON',
    statusPublished: '● PUBLIÉ',
    statusRetired: '○ RETIRÉ',
    footerCountTemplate: '{n} FRAMEWORKS · SANS PAGINATION (V1)',
    emptyTitle: 'Aucun framework ne correspond',
    emptyBody: 'Effacez les filtres ou créez un nouveau framework.',
    emptyMarketingTitle: 'Aucun framework pour l’instant',
    emptyMarketingBody:
      'Rédigez le premier parcours de bonnes pratiques pour l’un des 8 domaines.',
    loading: 'Chargement des frameworks…',
    listLoadError: 'Impossible de charger les frameworks. Réessayez.',
    // ─── Vue 7 · Éditeur ────────────────────────────────────────────────
    editorBreadcrumbFrameworkPlaceholder: 'NOUVEAU FRAMEWORK',
    editorTitlePlaceholder: 'Titre du framework',
    editorSaveDraft: 'Enregistrer',
    editorPublish: 'Publier',
    editorRepublish: 'Republier',
    editorRetire: 'Retirer',
    editorSaving: 'Enregistrement…',
    editorSaved: 'Enregistré',
    sectionMeta: 'Détails du framework',
    sectionMaturityTarget: 'Cible de maturité',
    sectionDeliverables: 'Livrables',
    sectionPhases: 'Phases & activités',
    // Cible de maturité — contrat d’aboutissement du framework
    maturityTargetDomainLabel: 'DOMAINE',
    maturityTargetDomainPlaceholder: 'Sélectionner un domaine…',
    maturityTargetLevelLabel: 'NIVEAU',
    maturityTargetLevelPlaceholder: 'Sélectionner un niveau…',
    maturityTargetHint:
      'Compléter ce framework fait progresser la maturité du tenant dans le domaine sélectionné jusqu’à ce niveau (basé sur les preuves — le tenant confirme le changement sur l’écran de fin).',
    maturityLevel: {
      initial: 'Initial',
      developing: 'En développement',
      established: 'Établi',
      advanced: 'Avancé',
      optimized: 'Optimisé',
    },
    fieldTitle: 'TITRE',
    fieldDomain: 'DOMAINE',
    fieldShortDescription: 'DESCRIPTION COURTE',
    fieldShortDescriptionHint: 'Résumé d’une ligne pour les cartes de la bibliothèque.',
    fieldLongDescription: 'DESCRIPTION DÉTAILLÉE',
    fieldEstimatedEffort: 'EFFORT ESTIMÉ',
    fieldEstimatedEffortPlaceholder: 'ex. 8 sem · 6 phases · ~25 activités',
    fieldTags: 'TAGS',
    fieldTagsPlaceholder: 'Tapez un tag puis Entrée…',
    // Livrables
    deliverablesIntro:
      'Les productions concrètes que le tenant obtient à la fin de ce framework. Produire chaque livrable est l’objet même du framework.',
    deliverablesEmptyPrompt:
      'Quelles productions concrètes le tenant obtient-il à la fin de ce framework ?',
    deliverablesEmptyExamples:
      'ex. Politique de gouvernance de l’IA · Registre des risques · Deck Conseil',
    deliverableSectionLabel: 'LIVRABLE',
    addDeliverableButton: '+ Ajouter un livrable',
    removeDeliverableButton: 'Supprimer le livrable',
    deliverableNameLabel: 'NOM',
    deliverableNamePlaceholder: 'ex. Politique de gouvernance de l’IA',
    deliverableDescriptionLabel: 'DESCRIPTION',
    deliverableRubricLabel: 'GRILLE D’ÉVALUATION (OPTIONNEL)',
    deliverableRubricPlaceholder:
      'Ce qui constitue un livrable « validé » — les critères à remplir.',
    deliverableRubricHint:
      'Optionnel. Utilisé par la boucle d’évaluation du coach (V1.x). Laisser vide pour un parcours en auto-déclaration.',
    // Rail persistant des livrables
    railDeliverablesTitle: 'LIVRABLES',
    railEmpty: 'Aucun déclaré pour l’instant.',
    railUnnamed: 'Livrable sans titre',
    railNoTemplateYet: 'Pas encore de modèle',
    phaseSectionLabel: 'PHASE',
    addPhaseButton: '+ Ajouter une phase',
    removePhaseButton: 'Supprimer la phase',
    phaseTitleLabel: 'TITRE',
    phaseDescriptionLabel: 'DESCRIPTION',
    phaseTagsLabel: 'TAGS',
    // Documents au niveau de la phase. Rôle verrouillé sur `reference`
    // (les modèles de livrables sont sur les activités).
    phaseDocumentsLabel: 'DOCUMENTS DE RÉFÉRENCE DE LA PHASE',
    phaseDocumentsEmpty:
      'Aucun document de référence sur cette phase — checklists, guides ou exemples que le tenant consulte avant d’exécuter les activités.',
    addPhaseDocumentButton: '+ Ajouter un document',
    removePhaseDocumentButton: 'Retirer le document',
    // Sélecteur de source (partagé avec les documents d’activité)
    docSourceKbEntry: 'Entrée KB',
    docSourceUpload: 'Fichier téléversé',
    docTitlePlaceholder: 'Titre du document (visible par le tenant)',
    docKbIdPlaceholder: 'Id d’entrée KB',
    docFileRefPlaceholder: 'Réf. du fichier téléversé',
    activitySectionLabel: 'ACTIVITÉ',
    addActivityButton: '+ Ajouter une activité',
    removeActivityButton: 'Supprimer l’activité',
    activityTitleLabel: 'TITRE',
    activityDescriptionLabel: 'DESCRIPTION',
    activityTagsLabel: 'TAGS',
    // Rédaction v2 des activités
    activityUntitled: 'Activité sans titre',
    activityDurationLabel: 'DURÉE',
    activityDurationPlaceholder: 'Sélectionner une durée…',
    activityDurationOverrideLabel: 'MODIFICATION PAR LE TENANT',
    activityDurationOverrideDescription:
      'Le tenant peut raccourcir ou allonger à l’activation.',
    durationBucket: {
      lt_1_month: 'Moins d’1 mois',
      '1_3_months': '1–3 mois',
      '3_6_months': '3–6 mois',
      '6_9_months': '6–9 mois',
      '9_12_months': '9–12 mois',
      '12_18_months': '12–18 mois',
      '18_24_months': '18–24 mois',
    },
    activityDependenciesLabel: 'DÉPENDANCES',
    activityDependenciesEmpty:
      'C’est la seule activité — rien à définir en dépendance pour l’instant.',
    activityDependenciesHint:
      'Les dépendances entre phases sont autorisées. Runtime = verrouillage doux avec aperçu.',
    activityStakeholdersLabel: 'PARTIES PRENANTES',
    activityStakeholdersPlaceholder: 'Tapez une partie prenante puis Entrée…',
    activityDocumentsLabel: 'DOCUMENTS',
    activityDocumentsEmpty:
      'Aucun document sur cette activité — guides de référence ou modèles de livrables.',
    addActivityDocumentButton: '+ Ajouter un document',
    removeActivityDocumentButton: 'Retirer le document',
    docSourceLabel: 'Source',
    docRoleLabel: 'Rôle',
    docRoleReference: 'Référence',
    docRoleDeliverableTemplate: 'Modèle de livrable',
    docProducesLabel: 'PRODUIT LES LIVRABLES',
    docProducesNoDeliverables:
      'Aucun livrable déclaré pour l’instant. Ajoutez-en un dans la section Livrables ci-dessus pour que ce modèle puisse le revendiquer.',
    docProducesHint: 'Un modèle peut alimenter plusieurs livrables.',
    railTemplateClaimed: 'Modèle rattaché',
    publishConfirmTitle: 'Publier ce framework ?',
    publishConfirmBody:
      'Les tenants le verront dans la bibliothèque et pourront l’activer. Vous pourrez le retirer plus tard — les instances en cours iront jusqu’au bout.',
    publishConfirmYes: 'Publier',
    publishConfirmCancel: 'Annuler',
    retireConfirmTitle: 'Retirer ce framework ?',
    retireConfirmBody:
      'Il ne sera plus disponible pour de nouvelles activations. Les tenants qui l’exécutent conservent leurs instances. Vous pourrez le republier plus tard.',
    retireConfirmYes: 'Retirer',
    retireConfirmCancel: 'Annuler',
    republishConfirmTitle: 'Republier ce framework ?',
    republishConfirmBody:
      'Il redevient activable pour de nouveaux tenants. Une nouvelle version est enregistrée ; les citations existantes restent figées sur leur version d’origine.',
    republishConfirmYes: 'Republier',
    republishConfirmCancel: 'Annuler',
    publishBlockedTitle: 'Pas prêt à publier',
    publishBlockedMaturityTargetRequired: 'Définir une cible de maturité (domaine + niveau).',
    publishBlockedNoDeliverables: 'Déclarer au moins un livrable.',
    publishBlockedDeliverableUnnamed: 'Chaque livrable doit avoir un nom.',
    publishBlockedDeliverableUnclaimed:
      'Chaque livrable doit être rattaché à au moins un modèle de livrable d’une activité.',
    publishBlockedAtLeastOnePhase: 'Au moins une phase est requise.',
    publishBlockedPhaseNeedsActivity:
      'Chaque phase doit contenir au moins une activité.',
    publishBlockedActivityDurationMissing:
      'Chaque activité doit avoir une durée définie.',
    fieldErrorRequired: 'Requis',
    detailGenericError: 'Une erreur est survenue. Réessayez.',
    detailNotFound: 'Framework introuvable.',
  },
  personae: {
    breadcrumbKnowledge: 'CONNAISSANCES',
    breadcrumbPersonae: 'PERSONAS',
    listTitle: 'Personas',
    newPersonaButton: '+ Nouveau persona',
    searchPlaceholder: 'Rechercher…',
    statTotal: 'PERSONAS',
    statActive: 'ACTIFS',
    statScenariosRun: 'SCÉNARIOS LANCÉS',
    statResponsesReviewed: 'RÉPONSES ÉVALUÉES',
    filterAll: 'Tous',
    filterActive: 'Actifs',
    filterArchived: 'Archivés',
    archivedBadge: 'Archivé',
    colName: 'Nom',
    colSector: 'Secteur',
    colSize: 'Taille',
    colJurisdiction: 'Juridiction',
    colScenarios: 'Scénarios',
    colUpdated: 'MAJ',
    emptyTitle: 'Aucun persona',
    emptyBody:
      'Créez votre premier profil de test pour évaluer le Coach IA.',
    editorCreateTitle: 'Nouveau persona',
    editorCreateSub:
      'Définissez une entreprise fictive + un profil GC pour tester le Coach IA.',
    editorEditSub: 'Modifier les attributs du persona.',
    editorLoadError: 'Échec du chargement.',
    editorSaveError: 'Échec de l’enregistrement.',
    cancelButton: 'Annuler',
    saveButton: 'Enregistrer',
    savingButton: 'Enregistrement…',
    groupIdentity: 'Identité',
    groupGeography: 'Géographie & juridiction',
    groupGCProfile: 'Profil du GC',
    groupMaturity: 'Maturité par domaine',
    groupBusinessContext: 'Contexte business',
    fieldName: 'Nom du persona',
    fieldNamePlaceholder: 'Northgate Legal',
    fieldSector: 'Secteur',
    fieldRevenueBand: 'Tranche de CA',
    fieldEmployeeBand: 'Effectif',
    fieldLegalDeptSize: 'Taille de la direction juridique',
    fieldGeographies: 'Zones d’activité (codes ISO, séparés par virgule)',
    fieldPrimaryJurisdiction: 'Juridiction principale',
    fieldYearsExperience: 'Années d’expérience',
    fieldBackground: 'Parcours',
    background_in_house: 'En interne',
    background_private_practice: 'Cabinet',
    background_mixed: 'Mixte',
    background_academic: 'Académique',
    background_other: 'Autre',
    maturityHelper: 'Renseignez les 8 avant d’enregistrer.',
    maturity_initial: 'Initial',
    maturity_developing: 'En développement',
    maturity_established: 'Établi',
    maturity_advanced: 'Avancé',
    maturity_optimized: 'Optimisé',
    domain_D1_strategy_org: 'D1 Stratégie & Organisation',
    domain_D2_external_counsel: 'D2 Conseils externes',
    domain_D3_legal_ops_tech: 'D3 Ops & Tech juridiques',
    domain_D4_team_dev: 'D4 Développement d’équipe',
    domain_D5_kpis_performance: 'D5 KPI & Performance',
    domain_D6_budget_finance: 'D6 Budget & Finance',
    domain_D7_stakeholder_comms: 'D7 Communication parties prenantes',
    domain_D8_governance_risk: 'D8 Gouvernance & Risque',
    contextHelper:
      'Décrivez la situation actuelle — événements récents, pressions réglementaires, priorités stratégiques. Ceci est injecté verbatim dans le prompt du Coach.',
    contextPlaceholder:
      'L’entreprise vient de finaliser une acquisition transfrontalière et fait face à une nouvelle pression réglementaire…',
    detailLoadError: 'Échec du chargement.',
    detailActiveSub: 'Persona de test actif.',
    detailArchivedSub: 'Archivé. Restaurez pour réactiver les tests.',
    editButton: 'Modifier',
    archiveButton: 'Archiver',
    archiveError: 'Échec de l’archivage.',
    restoreButton: 'Restaurer',
    restoreError: 'Échec de la restauration.',
    restoreSuperAdminOnly: 'Super Administrateur uniquement',
    openInLabsButton: 'Ouvrir dans Labs →',
    noContext: 'Aucun contexte business défini.',
    testScenariosTitle: 'Scénarios de test',
    noScenarios:
      'Aucun scénario. Ouvrez Labs pour en démarrer un.',
  },
  mapReview: {
    breadcrumbKnowledge: 'CONNAISSANCES',
    breadcrumbSources: 'DOCUMENTS SOURCES',
    breadcrumbMapReview: 'REVUE DU PLAN',
    loadingTitle: 'Chargement du plan…',
    loadingSub: 'Récupération du texte source + de la structure des chapitres.',
    loadErrorTitle: 'Impossible de charger le plan',
    loadErrorGeneric: 'Le plan n’a pas pu être chargé. Réessayez dans un instant.',
    proposingSub: 'Le proposeur IA analyse encore ce document. Rechargez dans ~60 s.',
    metaChapterLabel: 'CHAPITRE',
    metaOf: 'SUR',
    metaWords: 'MOTS',
    metaTokens: 'TOK',
    statusAwaiting: 'EN ATTENTE DE REVUE',
    statusApproved: 'APPROUVÉ · LECTURE SEULE',
    statusRejected: 'REJETÉ',
    footerReject: 'Rejeter',
    footerSave: 'Enregistrer',
    footerSaving: 'Enregistrement…',
    footerApprove: 'Approuver et ingérer',
    footerApproving: 'Approbation…',
    footerApproveDirtyTooltip: 'Enregistrez d’abord vos modifications, puis approuvez.',
    footerReadOnly: 'APPROUVÉ — lecture seule',
    footerDirty: 'MODIFICATIONS NON ENREGISTRÉES',
    footerApproveWithUnsavedTemplate: 'Approuver enregistrera vos {n} modifications',
    // Barre des modifications non enregistrées (source-map MR 6)
    unsavedBarCountTemplate: '{n} MODIFS NON ENREGISTRÉES',
    unsavedBarDescription:
      'Les modifications restent locales jusqu’à l’enregistrement. Approuver enregistre aussi.',
    unsavedBarDiscardAll: 'Tout annuler',
    unsavedBarSave: 'Enregistrer',
    unsavedBarSaveShortcut: '⌘S',
    // Confirmation "tout annuler"
    discardModalTitleTemplate: 'Annuler {n} modifications non enregistrées ?',
    discardModalBody:
      'Le plan revient à son dernier état enregistré. Les versions enregistrées ne sont pas affectées.',
    discardModalCancel: 'Annuler',
    discardModalConfirm: 'Annuler les modifications',
    // Toast d’enregistrement (MR 6)
    toastSavedTemplate: '{n} modifications enregistrées',
    // Barre de sélection (source-map MR 7)
    selectionToolbarAria: 'Actions sur la sélection',
    selectionCountTemplate: '{p} PARAGRAPHES · {w} MOTS',
    selectionSplitHere: 'Scinder ici',
    selectionSplitFirstParaBlocked:
      'La sélection est déjà au tout début de cette section — scinder ici produirait une moitié supérieure vide.',
    selectionSplitHereTagTemplate: 'SCINDER ICI · ~{before} / ~{after} tok',
    selectionMoveToTemplate: 'Déplacer vers {ref}',
    selectionRewrite: 'Réécrire',
    selectionInsertAfter: 'Insérer après',
    selectionExclude: 'Exclure',
    selectionActionComingSoon:
      'Les actions sur le contenu (Réécrire / Insérer / Exclure) arrivent au MR 7b avec le schéma de paragraphes.',
    // Toasts scinder / déplacer
    toastSplitTemplate: '{ref} scindée — nouvelle section ajoutée',
    toastMoveTemplate: 'Sélection déplacée vers {ref}',
    // ChunkSizeBadge — retour de tokens en direct (MR 9)
    chunkWas: 'avant',
    chunkVeryShort: 'très court',
    footerRejected: 'REJETÉ',
    footerRepropose: 'Reproposer le plan',
    footerReproposing: 'Reproposition en cours…',
    rejectModalTitle: 'Rejeter ce plan ?',
    rejectModalBody:
      'La proposition sera marquée comme rejetée. Vous pourrez la reproposer depuis zéro ensuite. Une raison aidera les futurs relecteurs à comprendre ce qui n’allait pas.',
    rejectModalReasonPlaceholder:
      'Raison facultative (ex. « Mauvaises limites de chapitre en Ch. 3–5 »)',
    rejectModalCancel: 'Annuler',
    rejectModalConfirm: 'Rejeter le plan',
    rejectModalConfirming: 'Rejet en cours…',
    leftPanePlaceholderTitle: 'ARBRE DE STRUCTURE',
    leftPanePlaceholderBody: 'L’éditeur d’arbre arrive au prochain MR de ce chantier.',
    rightPanePlaceholderTitle: 'TEXTE SOURCE',
    rightPanePlaceholderBody:
      'Le volet de droite affichera le texte source avec chaque section surlignée pour vérifier les limites et repérer les manques.',
    treeChapterPrefix: 'Chapitre',
    treeSubSectionPrefix: 'Sous-section',
    treeChapterPrefixUpper: 'CHAPITRE',
    treeSubSectionPrefixUpper: 'SOUS-SECTION',
    treeChapterTitleLabel: 'TITRE DU CHAPITRE',
    treeChapterTitlePlaceholder: 'Titre du chapitre tel qu’il apparaît dans le document…',
    treeSubSectionLabelPlaceholder: 'Libellé de sous-section…',
    treePatternDisclosureLabel: 'Modifier le motif de repérage',
    treePatternPlaceholder: 'Texte littéral du titre (4 à 8 premiers mots)…',
    treePatternHint:
      'Texte d’ancrage utilisé pour localiser ce titre dans le source. Insensible à la casse ; tolérant aux espaces. Doit être unique dans le chapitre.',
    treeAddSubSection: 'Ajouter une sous-section',
    treeDeleteSubSection: 'Supprimer la sous-section',
    treePrevChapter: 'Chapitre précédent',
    treeNextChapter: 'Chapitre suivant',
    treeNoMatchPill: 'NON TROUVÉ',
    treeNoMatchTooltip:
      'Le texte d’en-tête est introuvable dans le source extrait. Modifiez le motif ou vérifiez le texte source à droite.',
    treeExcludedPill: '⊘ HORS INGESTION',
    treeExcludedTooltip:
      'Cette sous-section est marquée pour être exclue de l’ingestion. Le chunker la sautera ; aucune entrée KB ne sera produite.',
    // Pastilles de statut d’édition
    editTagEdited: 'MODIFIÉ',
    editTagNew: 'NOUVEAU',
    editTagEditing: 'EN COURS',
    // Groupe d’actions par ligne. MR 8 réintroduit Scinder et Fusionner
    // maintenant que le modèle de données paragraph_overrides est en place.
    // Clés héritées — l'amas d'actions sur ligne a été supprimé le
    // 2026-10-21 (actions déplacées vers le menu flottant du volet droit).
    rowActionsAria: 'Actions de sous-section',
    rowActionSplit: 'Scinder',
    rowActionSplitHint:
      'Mettre cette section en focus — sélectionnez le texte dans le volet droit pour choisir le point de scission.',
    rowActionMergeUp: 'Fusionner',
    rowActionRename: 'Renommer',
    rowActionDelete: 'Supprimer',
    // Menu flottant de section (remplace l'amas d'actions sur ligne).
    sectionMenuAria: 'Actions de section',
    sectionMenuSplit: '✂ SCINDER',
    sectionMenuMergeUp: '↑ Fusionner',
    sectionMenuDelete: '× Supprimer',
    // Refonte Split (handoff 2026-10-26 : caret + aperçu) — la section
    // passe en mode « armé » via le bouton Scinder ; un caret clignotant
    // suit la souris entre les mots, le texte après le caret est teinté
    // pour prévisualiser la nouvelle section, et une carte collante
    // affiche le résultat avec un titre modifiable et l'ancre dérivée.
    // Le cas de découpe (3 sections) est une seconde coupe placée sous
    // la première. Chaînes miroir mot pour mot de
    // design/UI : UX v1.0/for-engineering-split-new/specs/
    // split-strings.md.
    splitBanner:
      'Cliquez entre deux mots là où la nouvelle section doit commencer.',
    splitCancel: 'Annuler · Échap',
    splitHintCut: '✂ cliquer pour couper',
    splitHintSentence: '✂ nouvelle phrase',
    splitGripNew: '✂ NOUVELLE SECTION',
    splitGripResume: '✂ {parent} REPREND',
    splitCardSplit: 'SCISSION · 2 SECTIONS',
    splitCardCarve: 'DÉCOUPE · 3 SECTIONS',
    splitCardDrag: 'glissez la coupe pour ajuster',
    splitKeeps: 'CONSERVE',
    splitNew: 'NOUVELLE SECTION',
    splitResumes: 'REPREND {parent}',
    splitResumesTitle: '{parent} (suite) · {firstWords}…',
    splitStartsAt: 'COMMENCE PAR',
    splitResumesAt: '{parent} REPREND À',
    splitUnique: '✓ UNIQUE',
    splitExtended: 'ÉTENDU À {n} MOTS',
    splitExtendedBody:
      'Les cinq premiers mots apparaissent ailleurs dans le chapitre ; l’ancre s’allonge jusqu’à être unique. Rien à corriger — déplacez la coupe si vous préférez un point plus distinctif.',
    splitCreate: 'Créer la section',
    splitCreateCarve: 'Créer 2 sections',
    splitEndFurther: '+ Terminer plus bas',
    splitEndFurtherPrompt:
      'Cliquez là où la nouvelle section s’arrête et où {parent} reprend.',
    splitRemoveEnd: 'Retirer la fin',
    splitPreviewTag: 'APERÇU',
    splitNewTag: 'NOUVELLE',
    splitSeam: '✂ SCINDÉ ICI · À L’INSTANT',
    splitToast: '{parent} scindée en deux sections',
    splitToastCarve: '{parent} découpée en trois sections',
    splitUndo: 'ANNULER',
    // Modèles de toast pour les nouvelles actions MR 8.
    toastRewriteTemplate: '{ref} réécrit — original conservé dans l’historique',
    toastInsertTemplate: '{ref} bloc rédigé inséré',
    toastExcludeParagraphTemplate: '{ref} paragraphe exclu de l’ingestion',
    toastMergeUpTemplate: '{ref} fusionné dans la section au-dessus',
    // Éditeur de paragraphe en ligne (Réécrire / Insérer) — ouvert
    // depuis la barre de sélection quand le relecteur choisit l’une
    // de ces deux actions.
    rewriteInsertPlaceholder:
      'Ajoutez du cadrage ou des mots-clés pour améliorer la vectorisation…',
    rewriteSave: 'Enregistrer',
    rewriteCancel: 'Annuler',
    rewriteOriginalKept: 'Original conservé dans l’historique',
    rewriteAriaRewrite: 'Réécrire le paragraphe',
    rewriteAriaInsert: 'Insérer un paragraphe',
    // Glisser-déposer de limite — étiquette au survol de la limite
    // de section ; le glissement déplace les paragraphes entre voisins.
    boundaryDragLabel: '⇕ Glissez pour déplacer cette limite',
    // MR 10 — UNDO en ligne sur le toast + tiroir d'historique.
    toastUndoLabel: 'Annuler',
    toastUndoShortcut: '⌘Z',
    historyToggleLabel: 'Historique',
    historyToggleAria: 'Ouvrir le tiroir d’historique des modifications',
    historyDrawerTitle: 'Historique des modifications',
    historyDrawerCloseAria: 'Fermer le tiroir d’historique',
    historyDrawerLoading: 'Chargement de l’historique…',
    historyDrawerEmpty: 'Aucune modification n’a encore été apportée à cette carte.',
    historyOp_create: 'Créé',
    historyOp_patch: 'Modifié',
    historyOp_propose_complete: 'Proposition de carte terminée',
    historyOp_propose_reset: 'Proposition de carte relancée',
    historyOp_approve: 'Approuvé',
    historyOp_reject: 'Rejeté',
    historyActor_system_propose_worker: 'Worker de proposition',
    historyActor_system_propose_worker_failure: 'Worker de proposition (échec)',
    historyActor_system_propose_invoke_failure: 'Système (échec d’invocation)',
    // En-tête de lecture collant du volet droit (orientation MR 5)
    readingLabel: 'LECTURE',
    readingHint: 'J / K pour naviguer',
    // Lecture par section focalisée (source-map MR 5b)
    docPanePrecedingSection: 'section précédente',
    docPaneFollowingSection: 'section suivante',
    docPaneSubNoMatchBody:
      'Le titre de cette sous-section est introuvable dans le texte source. Modifiez son motif de repérage à gauche pour pointer sur le vrai titre.',
    docPaneSubEmptyBody:
      'Aucun texte entre ce titre et le suivant.',
    docPaneChapterNoMatchTitle: 'Titre du chapitre introuvable dans le source',
    docPaneChapterNoMatchBody:
      'Le motif de repérage du chapitre n’a pas localisé de titre dans le texte extrait. Modifiez le motif à gauche pour pointer vers le titre réel, ou vérifiez si le texte source a été correctement extrait.',
    docPaneGapActionsAria: 'Actions sur le manque',
    docPaneSectionActionsAria: 'Actions sur la section',
    gapActionNewSection: '+ Nouvelle section à partir d’ici',
    gapActionExtendPrevious: '⊕ Prolonger la section précédente',
    gapActionMarkIntentional: '⊘ Exclure de l’ingestion',
    sectionActionExclude: '⊘ Exclure de l’ingestion',
    sectionActionUnexclude: '✓ Inclure dans l’ingestion',
    sectionActionDelete: '× Supprimer la sous-section',
    selectionActionNewSection: '+ Nouvelle section à partir de la sélection',
    docPaneNoMatchStripLabel: 'NON TROUVÉ',
    docPaneNoMatchStripSingle: 'Titre de sous-section attendu ici',
    docPaneNoMatchStripMulti: 'Titres de sous-section attendus ici',
  },
  labs: {
    breadcrumbLabs: 'GC LABS',
    title: 'GC Labs',
    subtitle:
      'Banc QA — simulez des questions GC, inspectez la récupération, vérifiez ce que le modèle a réellement utilisé.',
    queryPanelHeader: 'REQUÊTE',
    personaLabel: 'PERSONA',
    personaDefault: 'Aucun persona',
    noPersonasYet: 'Aucun persona pour l’instant',
    domainLabel: 'DOMAINE',
    domainAll: 'Tous les domaines',
    languageLabel: 'LANGUE',
    languageAll: 'Toutes',
    languageEn: 'EN',
    languageFr: 'FR',
    queryTextareaLabel: 'REQUÊTE DE TEST',
    queryTextareaHint: 'Entrée pour lancer · Maj+Entrée pour un saut de ligne',
    enrichCheckbox: 'Enrichir la requête de récupération',
    retrievalQueryLabel: 'REQUÊTE ENVOYÉE · CE QUI EST VECTORISÉ',
    retrievalQueryEmpty: '(vide)',
    retrievalQueryNotPreviewed:
      'Non prévisualisée — la requête brute ci-dessus sera vectorisée.',
    retrievalQueryEnrichedHint:
      'Transformée par Haiku à la demande — l’enrichissement s’exécute aussi au lancement',
    retrievalQueryRawHint: 'Requête brute — enrichissement désactivé',
    previewEnrichmentButton: 'Prévisualiser l’enrichissement',
    rePreviewEnrichmentButton: 'Reprévisualiser',
    previewingButton: 'Prévisualisation…',
    decomposeCheckbox: 'Décomposer les requêtes composées',
    decomposeHint:
      'Divise une question composée (ex. : « charge de travail ET efficacité ET performance ») en 2 à 3 sous-questions ciblées. Chacune est vectorisée et interrogée séparément ; les résultats sont unis. Corrige les cas où un seul embedding mixte reste sous le seuil de similarité et renvoie zéro.',
    subQueriesHeading: 'Sous-requêtes utilisées',
    compareCheckbox: 'Comparer avec / sans corpus',
    draftsCheckbox: 'Inclure brouillons et entrées en revue',
    minScoreLabel: 'Score de similarité minimum',
    minScoreHint:
      'Seuls les extraits ayant un score supérieur ou égal à ce seuil sont transmis au coach. Le résultat peut être plus petit que le plafond top-K — c’est voulu. Plage habituelle : 0,60–0,80. Mettre à 0 pour garder tous les extraits.',
    runButton: 'Lancer la requête',
    runningButton: 'En cours…',
    runningElapsedTemplate: 'Requête en cours… {n} s écoulées',
    // Progression de la requête (étapes cyclées pendant l’exécution)
    progressStageEnriching: 'Enrichissement de la requête',
    progressStageRetrieving: 'Récupération du contexte',
    progressStageGenerating: 'Génération de la réponse',
    progressElapsedTemplate: '{s} s écoulées',
    responseHeader: 'RÉPONSE',
    responseReadyTitle: 'Quand vous voulez',
    responseReadyBody: 'Saisissez une requête à gauche puis lancez-la.',
    responseSonnetAloneHeader: 'SONNET SEUL',
    responseWithCorpusHeader: 'AVEC CORPUS',
    responseErrorTitle: '⚠ ÉCHEC',
    responseErrorBody:
      'Bedrock a renvoyé une erreur de limitation après 2 tentatives. La récupération a abouti — seule la génération a échoué.',
    responseErrorRetry: 'Réessayer',
    orphanCalloutLabel: '⚠ CITATION ABSENTE DES RÉSULTATS',
    orphanCalloutBodyTemplate:
      'La réponse cite [{n}] alors que {m} extraits seulement ont été récupérés. Le modèle cite une source qu’il n’a pas reçue — considérez cette affirmation comme non étayée.',
    zeroRetrievalCalloutLabel: '⚠ RÉPONSE SANS CORPUS',
    zeroRetrievalCalloutBody:
      'Aucun extrait n’a été récupéré : cette réponse n’est pas ancrée — elle reflète les données d’entraînement du modèle, pas votre base de connaissances. Toute citation ici serait fabriquée par définition.',
    // Refus lié au périmètre du coaching (garde-fou)
    scopeDeclineResponseCalloutLabel: '· REFUS — HORS PÉRIMÈTRE DU COACHING',
    scopeDeclineResponseCalloutBody:
      'Le coach a décliné cette question conformément au contrat de coaching (conseil juridique, recherche juridique, avis sur le fond d’un litige, ou autre sujet hors périmètre). Il s’agit d’un comportement de garde-fou attendu, pas d’un échec. La réponse ci-dessus explique sur quoi le coach peut aider à la place.',
    retrievalHeader: 'RÉCUPÉRATION',
    scopeDeclineRetrievalTag: 'NON UTILISÉ',
    scopeDeclineRetrievalTitle: 'Récupération non utilisée pour cette réponse',
    scopeDeclineRetrievalBody:
      'Le coach a décliné selon le contrat de coaching : la récupération a bien été exécutée sur la requête, mais elle n’a pas servi de mécanisme pour répondre. Afficher les extraits récupérés donnerait une fausse image de ce que le coach a fait.',
    scopeDeclineRetrievalCountLabel: 'Ce qui aurait été récupéré',
    scopeDeclineHistoryTag: 'REFUSÉ',
    scopeDeclineHistoryTagTitle:
      'Le coach a décliné cette question selon le contrat de coaching — la récupération a été exécutée mais n’a pas servi à répondre.',
    metadataRetrievedTemplate: '{n} récupérés',
    metadataCitedTemplate: '{m}/{n} cités',
    metadataNoContext: 'sans contexte',
    metadataLatencyTemplate: '{s} s',
    citationChipTitle: 'cité',
    citationChipOrphanTitle: 'cité — mais absent des résultats',
    compareRetrievalCollapsedTemplate: 'RÉCUPÉRÉS · {n} — OUVRIR ›',
    retrievedHeaderTemplate: 'RÉCUPÉRÉS · {n} · TRIÉS PAR SCORE',
    citedChipTemplate: 'cités {m}/{n}',
    citedIndicatorTitle: 'cité',
    notCitedIndicatorTitle: 'non cité',
    expandCitedAsTemplate: 'cité en [{n}]',
    expandNotCited: 'non cité',
    expandChunkIdLabel: 'ID D’EXTRAIT',
    openInKb: 'Ouvrir dans la base ↗',
    retrievalEmpty: 'Les résultats apparaîtront ici après une requête.',
    retrievalZeroTitle: 'Aucun contenu correspondant',
    retrievalZeroBody:
      'Élargissez les termes, retirez les filtres domaine ou langue, ou vérifiez que les entrées concernées sont publiées.',
    retrievalZeroClearFilters: 'Effacer les filtres',
    retrievalZeroIncludeDrafts: 'Inclure brouillons',
    retrievalErrorNote:
      'La récupération a réussi — la génération a échoué. Ces extraits restent valides.',
    badgeQaPair: 'Q&R',
    badgeFramework: 'CADRE',
    badgeCaseStudy: 'CAS',
    badgeDocumentExcerpt: 'EXTRAIT',
    historyHeader: 'TESTS RÉCENTS',
    historyHeaderSubTemplate: '{n} derniers · cliquez pour charger les paramètres',
    colQuery: 'REQUÊTE',
    colPersona: 'PERSONA',
    colDomain: 'DOMAINE',
    colRetrieved: 'RÉCUP.',
    colCited: 'CITÉS',
    colBy: 'PAR',
    colWhen: 'QUAND',
    loadButton: 'Charger',
    deleteRowLabel: 'Supprimer',
    historyEmpty:
      'Aucun test. Lancez votre première requête pour constituer l’historique.',
    deleteConfirmTitle: 'Supprimer ce test ?',
    deleteConfirmBody: 'Il disparaîtra de l’historique partagé pour tout le monde.',
    deleteConfirmAction: 'Supprimer',
    deleteConfirmCancel: 'Annuler',
    errorGeneric: 'Le banc n’a pas pu terminer le test. Réessayez dans un instant.',
  },
  kb: {
    breadcrumbKnowledge: 'CONNAISSANCES',
    breadcrumbBase: 'BASE',
    title: 'Base de connaissances',
    subTemplate: '{total} entrées · {in_review} en revue · {draft} brouillons.',
    newEntryButton: '+ Nouvelle entrée',
    untitled: 'Entrée sans titre',
    statEntries: 'ENTRÉES',
    statPublished: 'PUBLIÉES',
    statInReview: 'EN REVUE',
    statDraft: 'BROUILLONS',
    chipAllTemplate: 'Toutes ({n})',
    chipPublishedTemplate: 'Publiées ({n})',
    chipInReviewTemplate: 'En revue ({n})',
    chipDraftTemplate: 'Brouillons ({n})',
    chipArchivedTemplate: 'Archivées ({n})',
    chipRemovedTemplate: 'Supprimées ({n})',
    typeFilterLabel: 'TYPE',
    typeFilterAll: 'Tous',
    domainFilterLabel: 'DOMAINE',
    domainFilterAll: 'Tous',
    langFilterLabel: 'LANGUE',
    langFilterAll: 'Toutes',
    searchPlaceholder: 'Rechercher dans la base…',
    colTitle: 'TITRE / QUESTION',
    colType: 'TYPE',
    colDomain: 'DOMAINE',
    colContributor: 'CONTRIBUTEUR',
    colVersion: 'VERSION',
    colUpdated: 'MAJ',
    colStatus: 'STATUT',
    colActions: 'ACTIONS',
    rowKebabAria: 'Actions de ligne',
    rowKebabDuplicate: '⎘ Dupliquer',
    rowKebabDuplicating: 'Duplication…',
    statusDraft: '● BROUILLON',
    statusInReview: '● EN REVUE',
    statusPublished: '● PUBLIÉ',
    statusArchived: '● ARCHIVÉ',
    statusRemoved: '○ SUPPRIMÉ',
    typeQaPair: 'Paire Q&R',
    typeFramework: 'Cadre',
    typeCaseStudy: 'Cas pratique',
    typeExcerpt: 'Extrait de document',
    domain: {
      D1_strategy_org: 'D1 — Stratégie & Org',
      D2_external_counsel: 'D2 — Conseils externes',
      D3_legal_ops_tech: 'D3 — Ops & Tech',
      D4_team_dev: 'D4 — Équipe & Talents',
      D5_kpis_performance: 'D5 — KPIs & Performance',
      D6_budget_finance: 'D6 — Budget & Finance',
      D7_stakeholder_comms: 'D7 — Parties prenantes & Comms',
      D8_governance_risk: 'D8 — Gouvernance & Risque',
    },
    footerCountTemplate: '{n} ENTRÉES',
    emptyTitle: 'Aucune entrée ne correspond',
    emptyBody: 'Essayez de retirer des filtres ou créez une nouvelle entrée.',
    emptyMarketingTitle: 'Aucune entrée pour l’instant',
    emptyMarketingBody: 'Commencez à constituer le corpus éditorial.',
    editorBreadcrumbNew: 'NOUVELLE',
    editorBreadcrumbEdit: 'MODIFIER',
    editorBackLink: '← Retour à la base',
    editorBackToList: '← Retour à la base',
    editorTitlePlaceholder: 'Entrée sans titre',
    editorTypeLabel: 'TYPE D’ENTRÉE',
    editorExcerptTooltip:
      'Les extraits proviennent des livres ingérés — révisés dans la file, non rédigés ici.',
    editorMetaDomain: 'DOMAINE',
    editorMetaLanguage: 'LANGUE',
    editorMetaContributor: 'CONTRIBUTEUR',
    editorMetaVersion: 'VERSION',
    editorVersionHistory: 'Historique des versions',
    editorVersionHistoryV1Note:
      'V1.0 : version actuelle uniquement. L’historique complet avec compteurs de citations arrive en V1.x.',
    editorCancel: 'Annuler',
    editorSaveDraft: 'Enregistrer le brouillon',
    editorSubmitForReview: 'Soumettre pour revue',
    editorPublish: 'Publier',
    editorArchive: 'Archiver',
    editorUnarchive: 'Désarchiver',
    editorRemove: 'Supprimer',
    editorDuplicate: '⎘ Dupliquer',
    editorDuplicateHint:
      'Cloner ce cas pratique en nouveau brouillon. La copie reprend le corps + métadonnées ; modifiez seulement les champs qui diffèrent (secteur, taille d’équipe, contexte).',
    editorRestore: 'Restaurer l’entrée',
    editorRestoreSuperAdminOnly:
      'Seuls les Super Administrateurs peuvent restaurer une entrée supprimée.',
    editorRestoreForbidden:
      'Restauration impossible — votre compte n’a pas la permission super_admin.',
    autosaveTemplate: 'Enregistré {when}',
    autosaveNever: 'Pas encore enregistré',
    fieldQuestion: 'QUESTION',
    fieldQuestionPlaceholder: 'Comment structurer mon panel de cabinets externes ?',
    fieldQuestionHint: 'Que demande l’utilisateur ? Utilisez la formulation canonique.',
    fieldQuestionVariants: 'VARIANTES DE QUESTION',
    fieldQuestionVariantsHint: 'Formulations alternatives pour la recherche.',
    fieldAddVariant: '+ Ajouter une variante',
    aiVariantsTrigger: 'Suggérer avec l’IA',
    aiVariantsDisabledHelper:
      'Renseignez d’abord la réponse — les suggestions s’appuient dessus.',
    aiVariantsPanelTitle: 'Variantes suggérées par l’IA',
    aiVariantsAcceptAll: 'Tout accepter',
    aiVariantsRejectAll: 'Tout rejeter',
    aiVariantsDismiss: 'Fermer',
    aiVariantsGroundingToggle: 'S’appuie sur cette réponse',
    aiVariantsRowAccept: 'Accepter',
    aiVariantsRowEdit: 'Modifier',
    aiVariantsRowConfirm: 'Valider',
    aiVariantsRowReject: 'Rejeter',
    aiVariantsProvenanceTag: 'IA',
    aiVariantsSuggestMore: 'Suggérer plus',
    aiVariantsSuggestMoreHelper:
      'Les nouvelles suggestions ne répètent pas les variantes conservées.',
    aiVariantsLoading: 'Génération des variantes…',
    aiVariantsLoadingSub:
      'Claude rédige quelques formulations — environ 5 secondes.',
    aiVariantsEmpty:
      'Aucune nouvelle formulation cette fois. Reformulez la question ou ajoutez des variantes manuellement.',
    aiVariantsError:
      'Service de suggestion indisponible. Vous pouvez continuer à ajouter des variantes manuellement.',
    aiVariantsErrorRetry: 'Réessayer',
    fieldAnswer: 'RÉPONSE',
    fieldAnswerPlaceholder: 'La réponse curée…',
    fieldAnswerHint:
      'La réponse curée. L’éditeur riche arrive en V1.x ; le markdown est accepté en V1.0.',
    fieldMethodologyName: 'NOM DE LA MÉTHODOLOGIE',
    fieldWhenToApply: 'QUAND L’APPLIQUER',
    fieldWhenToApplyPlaceholder:
      'ex. Le GC doit rationaliser un panel de plus de 30 cabinets externes en moins de 6 mois.',
    fieldWhenToApplyHint: 'Le déclencheur. Dans quelle situation cette méthodologie s’applique-t-elle ?',
    fieldPhases: 'PHASES',
    fieldPhasesHint:
      'Étapes séquentielles de la méthodologie. Ajoutez chaque phase avec ses activités et livrables.',
    fieldAddPhase: '+ Ajouter une phase',
    fieldPhaseNumberPrefix: 'PHASE',
    fieldRemovePhase: 'Supprimer la phase',
    fieldPhaseNamePlaceholder: 'Nom de la phase — ex. « Découverte & état des lieux »',
    fieldPhaseDescriptionPlaceholder: 'Que se passe-t-il dans cette phase…',
    fieldKeyActivities: 'ACTIVITÉS CLÉS',
    fieldAddActivity: '+ Ajouter une activité',
    fieldDeliverables: 'LIVRABLES',
    fieldAddDeliverable: '+ Ajouter un livrable',
    fieldArchetype: 'ARCHÉTYPE',
    fieldAnonymizedContext: 'CONTEXTE ANONYMISÉ',
    fieldAnonymizedContextHint:
      'Juste assez de détails pour que la recherche associe des profils GC similaires — aucune info identifiant le client.',
    fieldSector: 'SECTEUR',
    fieldSectorPlaceholder: 'ex. Tech / SaaS',
    fieldRevenueBand: 'TRANCHE DE CA',
    fieldRevenueBandPlaceholder: 'ex. 100 M€–500 M€',
    fieldDeptSize: 'TAILLE DE LA DIRECTION',
    fieldDeptSizePlaceholder: 'ex. 5–15 juristes',
    fieldGeography: 'GÉOGRAPHIE',
    fieldGeographyPlaceholder: 'ex. UE, ou FR / DE / BE',
    fieldSituation: 'SITUATION',
    fieldSituationPlaceholder: 'Quel était le contexte de départ ?',
    fieldDecision: 'DÉCISION',
    fieldDecisionPlaceholder: 'Quelle décision le GC a-t-il prise, et pourquoi ?',
    fieldOutcome: 'RÉSULTAT',
    fieldOutcomePlaceholder: 'Que s’est-il passé ensuite ? Indicateurs chiffrés si disponibles.',
    fieldLessons: 'LEÇONS',
    fieldAddLesson: '+ Ajouter une leçon',
    excerptSourceLabel: 'SOURCE',
    excerptTextLabel: 'EXTRAIT',
    excerptSuggestedDomainLabel: 'DOMAINE AUTO-CLASSIFIÉ',
    excerptReviewLabel: 'REVUE ÉDITORIALE',
    excerptApprove: 'Approuver',
    excerptRetag: 'Re-tagger le domaine',
    excerptReject: 'Rejeter',
    excerptRetagDomainLabel: 'NOUVEAU DOMAINE',
    excerptRetagConfirm: 'Appliquer le nouveau domaine',
    excerptRejectReasonLabel: 'MOTIF DU REJET (facultatif)',
    excerptRejectReasonPlaceholder:
      'ex. hors sujet ; recommande un avis juridique spécifique ; obsolète',
    excerptRejectConfirm: 'Rejeter l’extrait',
    excerptApprovedNote: '✓ Approuvé — publié pour la recherche.',
    excerptRejectedNote: '✕ Rejeté — archivé ; exclu de la recherche.',
    confirmYes: 'Confirmer',
    confirmCancel: 'Annuler',
    confirmArchiveTitle: 'Archiver cette entrée ?',
    confirmArchiveBody:
      'Elle sera masquée des réponses de coaching mais conservée pour l’audit. Vous pourrez la republier plus tard.',
    confirmRemoveTitle: 'Supprimer cette entrée ?',
    confirmRemoveBody:
      'La suppression est permanente. L’entrée reste dans l’historique des versions mais est masquée des vues par défaut et exclue des réponses de coaching. Seul un Super Administrateur peut la restaurer.',
    confirmRemoveReasonLabel: 'Motif (facultatif)',
    confirmRemoveReasonPlaceholder: 'ex. remplacée par l’entrée #4012',
    confirmRestoreTitle: 'Restaurer l’entrée supprimée ?',
    confirmRestoreBody:
      'Cela ramène l’entrée à l’état de brouillon. La suppression initiale reste dans le journal d’audit.',
    confirmRestoreTypeLabel: 'Tapez RESTORE (en majuscules) pour confirmer :',
    confirmRestoreLabel: 'Restaurer l’entrée',
    editorGenericError: 'Une erreur est survenue. Réessayez.',
    editorRequiredToPublish: 'Complétez les champs suivants avant publication : {fields}.',
    editorRequiredToSubmit:
      'Complétez les champs suivants avant soumission à la revue : {fields}.',
  },
  kbSearch: {
    breadcrumbInsights: 'ANALYSES',
    breadcrumbRag: 'PIPELINE RAG',
    title: 'Pipeline RAG — Test de recherche',
    subTitle: 'Vérifiez que les entrées créées remontent pour les bonnes requêtes. La composition de recommandations + plans à partir de la recherche arrive en V1.x.',
    queryLabel: 'REQUÊTE',
    queryPlaceholder: 'ex. comment structurer mon panel de cabinets externes ?',
    typeLabel: 'TYPE',
    typeAll: 'Tous les types',
    domainLabel: 'DOMAINE',
    domainAll: 'Tous les domaines',
    langLabel: 'LANGUE',
    langAll: 'Les deux',
    topKLabel: 'TOP K',
    includeUnpublishedLabel: 'Inclure brouillons / en revue / archivées',
    submitButton: 'Rechercher →',
    hint: 'La requête est encodée par Bedrock Titan v2 puis classée par similarité cosinus.',
    idleTitle: 'Tapez une requête ci-dessus',
    idleBody: 'Chaque résultat renvoie à son entrée. Cliquez sur une ligne pour l’ouvrir dans un nouvel onglet.',
    errorTitle: 'Échec de la recherche',
    errorUpstream: 'Bedrock est temporairement indisponible. Réessayez dans un instant.',
    errorGeneric: 'Une erreur est survenue. Réessayez.',
    noResultsTitle: 'Aucun résultat',
    noResultsBody: 'Rien dans la base ne correspond à « {query} ». Essayez d’élargir les filtres, d’inclure les entrées non publiées, ou de reformuler.',
    resultsCount: '{n} RÉSULTATS',
    openEntryTooltip: 'Ouvrir cette entrée dans un nouvel onglet',
  },
  review: {
    pageTitle: 'File de validation éditoriale',
    pageSubTemplate:
      '{count} extraits en attente de validation · triés par confiance croissante',
    sortLabel: 'TRI',
    sortConfidenceAsc: 'Confiance ↑ (les plus faibles d’abord)',
    sortConfidenceDesc: 'Confiance ↓ (les plus élevées d’abord)',
    sortUpdated: 'Mis à jour (le plus récent)',
    domainLabel: 'DOMAINE',
    domainAll: 'Tous les domaines',
    refreshButton: 'Rafraîchir',
    loading: 'Chargement des extraits…',
    emptyTitle: 'File vide',
    emptyBody:
      'Aucun extrait à valider. Ingérez un document source ou modifiez les filtres.',
    confidenceTemplate: 'confiance {pct} %',
    editedBadge: 'Modifié',
    approveButton: 'Approuver',
    editButton: 'Modifier l’extrait',
    retagButton: 'Changer de domaine',
    rejectButton: 'Rejeter',
    cancelButton: 'Annuler',
    saveButton: 'Enregistrer',
    editLabel: 'TEXTE DE L’EXTRAIT',
    editHint:
      'Enregistrer ajoute le badge "Modifié". Les citations indiqueront "Adapté de..." pour préserver la provenance.',
    retagLabel: 'NOUVEAU DOMAINE',
    rejectReasonLabel: 'RAISON (FACULTATIVE)',
  },
  sources: {
    breadcrumbKnowledge: 'CONNAISSANCES',
    breadcrumbSources: 'SOURCES',
    title: 'Documents sources',
    subTemplate: '{total} documents · {processing} en traitement · {ingested} ingérés.',
    uploadButton: '+ Téléverser un livre',
    statTotal: 'DOCUMENTS',
    statUploaded: 'TÉLÉVERSÉS',
    statProcessing: 'EN TRAITEMENT',
    statIngested: 'INGÉRÉS',
    chipAllTemplate: 'Tous ({n})',
    chipUploadedTemplate: 'Téléversés ({n})',
    chipProcessingTemplate: 'En traitement ({n})',
    chipIngestedTemplate: 'Ingérés ({n})',
    chipFailedTemplate: 'En échec ({n})',
    chipRemovedTemplate: 'Supprimés ({n})',
    langFilterLabel: 'LANGUE',
    langFilterAll: 'Toutes',
    searchPlaceholder: 'Rechercher titre ou auteur…',
    colTitle: 'TITRE',
    colAuthor: 'AUTEUR',
    colLang: 'LANGUE',
    colStatus: 'STATUT',
    colChunks: 'EXTRAITS',
    colSize: 'TAILLE',
    colUploadedBy: 'TÉLÉVERSÉ PAR',
    colUploaded: 'TÉLÉVERSÉ',
    footerCountTemplate: '{n} DOCUMENTS',
    statusUploaded: '● TÉLÉVERSÉ',
    statusProcessing: '● EN TRAITEMENT',
    statusIngested: '● INGÉRÉ',
    statusFailed: '● EN ÉCHEC',
    statusRemoved: '○ SUPPRIMÉ',
    statusMessageUploaded:
      'Le fichier est stocké. Déclenchez l’ingestion lorsque vous êtes prêt.',
    statusMessageProcessing:
      'L’ingestion est en cours. Les extraits apparaîtront une fois terminée.',
    statusMessageIngested:
      '{n} extraits créés — disponibles dans la file de revue de la KB.',
    statusMessageFailed: 'L’ingestion a échoué. Consultez l’erreur ci-dessous et relancez.',
    statusMessageRemoved:
      'Ce document a été supprimé (soft-delete). Restaurez-le pour le réutiliser.',
    emptyTitle: 'Aucun document ne correspond',
    emptyBody: 'Essayez de retirer des filtres ou téléversez un nouveau document.',
    emptyMarketingTitle: 'Aucun document source pour l’instant',
    emptyMarketingBody:
      'Téléversez les livres d’Olivier, des rapports ou des articles. Le pipeline d’ingestion les découpe en extraits pour la KB.',
    uploadBreadcrumb: 'TÉLÉVERSEMENT',
    uploadTitle: 'Téléverser un document source',
    uploadSubTitle:
      'Renseignez les métadonnées, déposez le fichier. Après téléversement, vous pourrez lancer l’ingestion depuis la page de détail.',
    uploadSuccess: 'Téléversé — ouverture de la page de détail.',
    uploadCancel: 'Annuler',
    uploadSubmit: 'Téléverser →',
    uploadErrorNoFile: 'Choisissez un fichier d’abord.',
    uploadErrorTooLarge: 'Fichier trop volumineux — limite de 100 Mo.',
    uploadErrorBadType:
      'Type de fichier non supporté. Acceptés : .docx, .txt. Pour un fichier .doc, ouvrez-le dans Word et Enregistrer sous → Document Word (.docx). Le support PDF arrive bientôt.',
    uploadErrorMissingMetadata: 'Le titre et l’auteur sont obligatoires.',
    uploadErrorCreateFailed:
      'Impossible de créer le document. Réessayez.',
    uploadErrorPutFailed:
      'Le téléversement du fichier a échoué. Réessayez depuis la page de détail.',
    fieldTitle: 'TITRE',
    fieldTitlePlaceholder: 'L’impact de l’IA sur les juristes',
    fieldAuthor: 'AUTEUR',
    fieldAuthorPlaceholder: 'Olivier Chaduteau',
    fieldPrefaceBy: 'PRÉFACE PAR',
    fieldPrefaceByPlaceholder: 'Kami Haeri',
    fieldYear: 'ANNÉE DE PUBLICATION',
    fieldYearPlaceholder: '2024',
    fieldIsbn: 'ISBN',
    fieldIsbnPlaceholder: '978-1-2345-6789-0',
    fieldLanguage: 'LANGUE',
    fieldFilename: 'NOM DE FICHIER',
    fieldFileSize: 'TAILLE',
    fieldChunkCount: 'EXTRAITS CRÉÉS',
    fieldUploadedBy: 'TÉLÉVERSÉ PAR',
    fieldIngestedAt: 'INGÉRÉ LE',
    fieldIngestionError: 'ERREUR D’INGESTION',
    dropzoneTitle: 'Déposez le fichier ici',
    dropzoneBody: 'Ou cliquez pour parcourir.',
    dropzoneFormats: '.docx · .txt',
    dropzoneReplaceHint: 'Cliquez pour remplacer.',
    detailSubTemplate: 'par {author}',
    backToList: '← Retour aux documents',
    backToDetail: '← Retour au document',
    metaSectionTitle: 'Métadonnées',
    editMetadata: 'Modifier les métadonnées',
    editCancel: 'Annuler',
    editSave: 'Enregistrer',
    triggerIngestionButton: 'Lancer l’ingestion →',
    retryIngestionButton: 'Relancer l’ingestion →',
    removeButton: 'Supprimer',
    restoreButton: 'Restaurer',
    restoreSuperAdminOnly:
      'Seuls les Super Administrateurs peuvent restaurer un document supprimé.',
    restoreForbidden:
      'Restauration impossible — votre compte n’a pas la permission super_admin.',
    confirmYes: 'Confirmer',
    confirmCancel: 'Annuler',
    confirmRemoveTitle: 'Supprimer ce document source ?',
    confirmRemoveBody:
      'Le document et ses extraits restent dans le système mais sont masqués des vues par défaut. Seul un Super Administrateur peut restaurer.',
    confirmRemoveReasonLabel: 'Motif (facultatif)',
    confirmRemoveReasonPlaceholder: 'ex. doublon de #1234',
    confirmRestoreTitle: 'Restaurer ce document source ?',
    confirmRestoreBody:
      'Cela ramène le document à l’état téléversé. Pour réingérer, lancez l’ingestion ensuite.',
    genericError: 'Une erreur est survenue. Réessayez.',
    notFound: 'Document source introuvable.',
    cancel: 'Annuler',
  },
  mapping: {
    cardTitle: 'Structure du document',
    noStructureBody:
      "Avant l’ingestion, proposez une carte de chapitres et sous-sections. Claude Sonnet 4.6 lit le document et en suggère une à l’équipe éditoriale.",
    proposeButton: 'Proposer la structure',
    chunkSizeTooltipChunk: {
      tiny: 'Trop petit (<200 tokens). Ce chunk peut manquer de contexte autonome. Envisagez de le fusionner avec une section adjacente, ou d’élargir le motif de titre.',
      good: 'Bonne taille. Ce chunk s’embarquera proprement et permettra une récupération haute-fidélité.',
      large:
        'Grand (1 500 à 3 000 tokens). La récupération peut ramener plusieurs concepts à la fois. Envisagez de diviser en 2 sous-sections pour des réponses plus nettes.',
      oversized:
        'Surdimensionné (>3 000 tokens). Le chunker le divisera automatiquement aux frontières de paragraphes à l’ingestion, ce qui peut nuire à la cohérence. Diviser en sous-sections plus serrées donne de meilleurs résultats.',
    },
    chunkSizeTooltipChapter: {
      tiny: 'Ce chapitre est très court (<200 tokens tous sous-sections confondues).',
      good: 'Taille totale du chapitre saine — la plupart des sous-sections devraient s’embarquer proprement.',
      large:
        'Chapitre volumineux (1 500 à 3 000 tokens au total). Vérifiez les badges des sous-sections.',
      oversized:
        'Chapitre très volumineux (>3 000 tokens au total). Si les sous-sections sont aussi signalées, resserrez-les ; sinon, le chunker divisera les sous-sections individuelles à l’ingestion.',
    },
    retryButton: 'Réessayer',
    reviewButton: 'Examiner & approuver',
    reproposeButton: 'Reproposer',
    viewButton: 'Voir la structure',
    proposingTitle: 'Analyse du document…',
    proposingSub:
      'Lecture du texte intégral et détection des chapitres + sous-sections. Environ 60 secondes.',
    loading: 'Chargement…',
    proposeFailedTitle: 'La proposition a échoué',
    summaryTemplate: '{chapters} chapitres · {subSections} sous-sections',
    proposedByModel: 'Proposé par {model}',
    statusProposing: 'EN COURS',
    statusError: 'ERREUR',
    statusPendingReview: 'À EXAMINER',
    statusApproved: 'APPROUVÉ',
    statusRejected: 'REJETÉ',
    breadcrumbMap: 'Structure',
    pageTitleTemplate: 'Structure · {title}',
    pageSub: 'Examinez la carte proposée. Modifiez puis approuvez pour lancer l’ingestion.',
    emptyChapters: 'Aucun chapitre pour le moment. Ajoutez-en un pour commencer.',
    addChapter: '+ Ajouter un chapitre',
    addSubSection: 'Ajouter une sous-section',
    deleteChapter: 'Supprimer le chapitre',
    deleteSubSection: 'Supprimer la sous-section',
    chapterNumberPrefix: 'Chapitre',
    chapterTitleLabel: 'TITRE',
    chapterTitlePlaceholder: 'p. ex. Bienvenue dans le monde de demain',
    headerPatternLabel: 'MOTIF D’EN-TÊTE',
    headerPatternPlaceholder: 'Les premiers mots de l’en-tête tels qu’ils apparaissent',
    headerPatternHint:
      'Le chunker utilise ce motif comme ancre regex. Préservez exactement la casse, les accents et la ponctuation.',
    subSectionPrefix: 'Sous-section',
    subSectionLabelPlaceholder: 'p. ex. 1.A · La rupture digitale',
    saveButton: 'Enregistrer',
    approveAndIngestButton: 'Approuver & ingérer',
    rejectButton: 'Rejeter',
    rejectConfirmTitle: 'Rejeter cette structure ?',
    rejectConfirmBody:
      'La structure reste enregistrée comme rejetée (pour l’audit). Pour réessayer, reproposez depuis la page du document.',
    rejectReasonLabel: 'RAISON (FACULTATIVE)',
    rejectConfirmAction: 'Rejeter',
    approveConfirmTitle: 'Approuver et lancer l’ingestion ?',
    approveConfirmBody:
      'La carte est verrouillée une fois approuvée. L’ingestion utilisera exactement cette structure. ~210 extraits arriveront dans la file de validation éditoriale.',
    approveConfirmAction: 'Approuver & ingérer',
  },
  settings: {
    title: 'Paramètres',
    lead: 'Réglages produit pour toute la plateforme. Les changements s’appliquent à tous les clients et sont consignés dans le journal d’audit.',
    group: {
      coach: 'RÉPONSES DU COACH',
      content: 'OPÉRATIONS DE CONTENU',
      experimental: 'EXPÉRIMENTAL',
    },
    tag: {
      experimental: 'EXPÉRIMENTAL',
    },
    state: {
      on: 'ACTIF',
      off: 'INACTIF',
    },
    audit: 'Dernière modification par {name} · {when}',
    neverChanged: 'Par défaut — jamais modifié',
    history: 'Historique',
    hideHistory: 'Masquer l’historique',
    historyLoading: 'Chargement…',
    historyEmpty: 'Aucun changement pour l’instant.',
    historyEntry: '{name} l’a {state}',
    cancel: 'Annuler',
    sources: {
      label: 'Afficher les sources dans les réponses',
      desc: 'Montre aux clients les entrées de la base sur lesquelles s’appuie chaque réponse : ligne de sources, panneau des sources et repères numérotés. Désactivé, les trois sont masqués et le coach ne cite plus dans le texte.',
      learning: 'Ce réglage pourrait devenir une préférence individuelle.',
      effect: 'S’APPLIQUE AUX NOUVELLES RÉPONSES · TOUS LES CLIENTS · RÉPONSES EXISTANTES INCHANGÉES',
      confirmOn: {
        title: 'Afficher les sources pour tous les clients ?',
        body: 'Les nouvelles réponses indiqueront les entrées de la base utilisées.',
      },
      confirmOff: {
        title: 'Masquer les sources pour tous les clients ?',
        body: 'Les nouvelles réponses n’afficheront plus la ligne de sources, le panneau ni les repères. Les réponses déjà données restent inchangées.',
      },
      hide: 'Masquer les sources',
      show: 'Afficher les sources',
    },
    error: {
      load: 'Impossible de charger les paramètres. Réessayez.',
      save: 'Impossible d’enregistrer. Réessayez.',
    },
  },
} satisfies StringTree;

const LOCALES: Record<Lang, typeof en> = { EN: en, FR: fr };

function get(tree: StringTree, key: string): string {
  const path = key.split('.');
  let node: string | StringTree = tree;
  for (const segment of path) {
    if (typeof node !== 'object' || !(segment in node)) {
      // Degrade gracefully: a missing key logs a warning and returns the raw
      // key as its own label. Throwing from here blanks the whole page
      // (React unwinds the render tree), which is a disproportionate cost
      // for a translation gap. The raw key is at worst ugly; a blank page
      // is a bug ticket. Collecting the warnings in Sentry/CloudWatch gets
      // the gap fixed on the next deploy without taking the UI down.
      console.warn(`[i18n] key not found: ${key}`);
      return key;
    }
    const next: string | StringTree | undefined = (node as StringTree)[segment];
    if (next === undefined) {
      console.warn(`[i18n] key not found: ${key}`);
      return key;
    }
    node = next;
  }
  if (typeof node !== 'string') {
    console.warn(`[i18n] key is not a leaf: ${key}`);
    return key;
  }
  return node;
}

/**
 * Simple {placeholder} substitution. Caller passes a values record; missing
 * placeholders are left as-is (loud-by-default so missing data is visible
 * during dev).
 */
function interp(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, key) =>
    key in values ? String(values[key]) : m,
  );
}

interface I18nApi {
  lang: Lang;
  setLang: (next: Lang) => void;
  t: (key: string) => string;
  /** Resolve a template key and substitute {placeholders}. */
  tt: (key: string, values: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nApi | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>('EN');
  const api = useMemo<I18nApi>(
    () => ({
      lang,
      setLang,
      t: (key) => get(LOCALES[lang], key),
      tt: (key, values) => interp(get(LOCALES[lang], key), values),
    }),
    [lang],
  );
  return <I18nContext.Provider value={api}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nApi {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}
